import { biConnectionApiSlice } from "./slice/bi-connection.js";
import { languageMaturityOfWire, voiceGenderOfWire } from "./catalogue-view.js";
import { voiceCatalogueText } from "../i18n/voice-view.js";
import { agentTemplateText, localizeAgentTemplateCard } from "../i18n/template-view.js";
import { portalText } from "../i18n/portal.js";
import { validatedDisplayView, extendDisplayView } from "../i18n/display-view.js";
import { portalLocale } from "../i18n/portal.js";
import { entryGuardOrSolved } from "./identity-challenge.js";
/**
 * The gateway that talks to a running WeTalk API.
 *
 * Every method below is one of two things and never anything in between:
 *
 *   a real call through `WeTalkApiClient`, for an operation the assembled spec documents; or
 *   a throw of {@link UndocumentedOperationError}, naming the `operationId` that is missing.
 *
 * There is no third branch. A console that quietly returned an empty list for a route that
 * does not exist would look like "no conversations yet" rather than "this was never built",
 * and the difference matters most in exactly the situation where it is hardest to notice.
 *
 * AMENDED 2026-09-22 (plan-002 RB-03, IC-2): the areas plan-002 closes are no longer written
 * out here. Each is a slice under `./slice/<area>.ts`, spread into the object below, which
 * refuses (or, once its unit lands, calls) exactly as this file did. A Track C unit edits its
 * own slice and never this file.
 */
import type { AccountRole } from "@wetalk/domain";
import {
  AGENT_TEMPLATE_CATALOGUE,
  ACCOUNT_ROLE,
  MEMBER_ROLE,
  isAccountCapability,
  isWorkspaceCapability,
  money,
  type AgentTemplateField,
  type ConversationDirection,
  type CurrencyCode,
  type DiscoveryOutcome,
  type Money,
} from "@wetalk/domain";
// `ApiCredential` is the generated client's own type name. It is aliased rather than
// used bare so this console declares `person_credential`, the approved compound.
import { ApiError, WeTalkApiClient, assertOk } from "@wetalk/api-client";
// glossary-lint-disable-next-line credential -- the generated client's own exported type name
import type { ApiCredential as ApiPersonCredential } from "@wetalk/api-client";

import {
  MalformedResponseError,
  readArray,
  readBoolean,
  readDataArray,
  readRecord,
  readText,
  readTextArray,
  readWholeNumber,
} from "./decode.js";
import {
  answeringOfWire,
  channelBindingOfWire,
  concurrencyOfWire,
  connectionOfWire,
  mediumDetailOfWire,
  mediumRowOfWire,
  numberActivityOfWire,
  numberPageOfWire,
  regionLatencyOfWire,
  signallingOfWire,
  widgetOfWire,
  workspaceSipOfWire,
  channelUnitLimitApiSlice,
} from "./channel-view.js";
import {
  channelLabelOptionOfWire,
  customerOrderOfWire,
  elapsedSecondsOf,
  formatWhen,
  historyChannelLabelWireOf,
  historyOutcomeWireOf,
  messagingConversationRowOfWire,
  phaseOf,
  readMedium,
  readMoney,
  historyStatOf,
  readNullableText,
  readOutboundOrigin,
  mediumSplitOfWire,
  recordingOfWire,
  rowOfWire,
  segmentOfWire,
  transcriptOfWire,
} from "./conversation-view.js";
import { conversationHandoffOfWire, takenMessageListOfWire } from "./taken-message-view.js";
import {
  agentVersionOfWire,
  analyticsOfWire,
  analyticsWindowOf,
  conversationFlagOfWire,
  forwardFactOfWire,
  gapOfWire,
  testSuiteOfWire,
  voiceAgentOfWire,
  voiceAgentListItemOfWire,
  voiceAgentRecordingOfWire,
} from "./voice-agent-view.js";
import {
  AuthSessionUnscopedError,
  entryGuardBodyOf,
  type EntryGuardView,
  ImpersonationDetailUnavailableError,
  type AgentTemplateCardView,
  type AuthSessionActorView,
  type AuthSessionNoticeView,
  type AuthSessionView,
  type AccountChoiceView,
  type AccountStateView,
  type ContextCapabilityView,
  type LandingView,
  type ScopeTargetView,
  type SwitchedScopeView,
  type WorkspaceChoiceView,
  type WorkspaceRoleView,
  type LogInRequestView,
  type AgentVersionSummaryView,
  type AnalyticsRange,
  type ApiKeyDraft,
  type ApiKeyView,
  type AutoReloadView,
  type BillingStateView,
  type ChargeAttemptView,
  type ChargeAttemptState,
  type ConsoleGateway,
  type CreditBalanceView,
  type WorkspaceBudgetView,
  type BillingRefusalView,
  type CreditState,
  type ConversationFlagView,
  type CreatedVoiceAgentView,
  type GapView,
  type PublishedAnswersView,
  type TestSuiteView,
  type VoiceAgentAnalyticsView,
  type VoiceAgentDraft,
  type HistoryQuery,
  type HistoryStatView,
  type InvoiceView,
  type IssuedApiKeyView,
  type IssuedInvitationView,
  type LanguagePackView,
  type LedgerEntryView,
  type LedgerPageView,
  type MemberRoleView,
  type WorkspaceInvitationState,
  type LiveBoardConversationView,
  type LiveSnapshotView,
  type PastConversationRowView,
  type NumberAvailabilityView,
  type PastConversationPageView,
  type PastConversationView,
  type PaymentMethodView,
  type SpendControlView,
  type StreamListener,
  type StreamSubscription,
  type TierView,
  type TopUpOutcomeView,
  type TopUpRequest,
  type UsageDayView,
  type VoiceAgentView,
  type VoiceView,
  type WebhookEndpointView,
  type WorkspaceDetailView,
  type WorkspaceInvitationView,
  type WorkspaceMemberView,
  type WorkspacePeopleView,
  type VoiceAgentConnectionView,
  type AnsweringScheduleView,
  type ChannelBindingRowView,
  type ConcurrencySummaryView,
  type MessagingMediumDetailView,
  type MessagingMediumRowView,
  type NumberActivityView,
  type NumberPageView,
  type RegionLatencyView,
  type SignallingEventView,
  type SipRotationView,
  type WebChatConversationRowView,
  type WebChatWidgetView,
  type WorkspaceSipCredentialView,
  type WorkspaceView,
} from "./gateway.js";
import { accountApiSlice } from "./slice/account.js";
import { agentDocumentApiSlice } from "./slice/agent-document.js";
import { voiceAgentLifecycleApiSlice } from "./slice/voice-agent-lifecycle.js";
import { answeringBodyOf, voiceAgentEditApiSlice } from "./slice/voice-agent-edit.js";
import { auditEntryApiSlice } from "./slice/audit-entry.js";
import { recordingApiSlice } from "./slice/recording.js";
// Design §26: the voice chooser's Play — the one operation answered as audio.
import { voiceSampleApiSlice } from "./slice/voice-sample.js";
import { liveActivityApiSlice } from "./slice/live-activity.js";
import { createVoiceAgentBodyVoiceOf, wizardApiSlice } from "./slice/wizard.js";
import { billingCardApiSlice } from "./slice/billing-card.js";
import { campaignApiSlice } from "./slice/campaign.js";
import { complianceApiSlice } from "./slice/compliance.js";
import { contactListApiSlice } from "./slice/contact-list.js";
import { doNotCallApiSlice } from "./slice/do-not-call.js";
import { messagingApiSlice } from "./slice/messaging.js";
import { notificationApiSlice } from "./slice/notification.js";
import { pendingRequestApiSlice } from "./slice/pending-request.js";
import { optimisationReviewApiSlice } from "./slice/optimisation-review.js";
import type { SliceContext } from "./slice/slice-context.js";
import { surveyApiSlice } from "./slice/survey.js";
import { testCallApiSlice } from "./slice/test-call.js";
import { workspaceHomeApiSlice } from "./slice/workspace-home.js";
import { impersonationHandoffApiSlice } from "../routes/impersonation/impersonation-handoff-gateway.js";
// plan-014 F3d: the confirmation of an operator action (an optional member, like the support hand-off).
import { operatorActionConfirmationApiSlice } from "../routes/confirm-operator-action/confirm-operator-action-gateway.js";
// plan-014 GAP: a person's own emailed links (address verification, email change) — optional members likewise.
import { mailLandingApiSlice } from "../routes/mail-landing/mail-landing-gateway.js";
import { passwordResetApiSlice } from "../routes/password-reset/password-reset-gateway.js";
// plan-014 GAP: the shared agent configurations a new agent may start from (an optional member likewise).
import { sharedConfigurationApiSlice } from "../routes/agents/new/shared-configuration-gateway.js";
// plan-014 GAP: the purchase terms a plan purchase names (an optional member likewise).
import { purchaseTermsApiSlice } from "../routes/finance/plans/purchase-terms-gateway.js";
import { openEventStream, streamRegistry, streamUrlOf } from "./stream.js";
// plan-014 A4: the scope generation every request carries, and the late answers it discards.
import { createScopeGeneration, isScopeGeneration } from "../scope/generation.js";
import { scopedFetch } from "../scope/scoped-fetch.js";
import { defaultScopeTargetOf } from "../scope/target.js";
import { orderBookApiGateway } from "../routes/agents/[id]/orders/order-book-api.js";
import { customerOrderPaymentApiSlice } from "./slice/customer-order-payment.js";
import { payoutApiSlice } from "./slice/payout.js";
import { sipTrunkApiSlice } from "./slice/sip-trunk.js";
import { outboundConversationApiSlice } from "./slice/outbound-conversation.js";
// plan-014 REG (ED-15): the twelve customer areas of design §27.9.2, spread once below.
import { workspaceManagementApiSlice } from "./slice/workspace-management.js"; // plan-014 B3
import { accountTeamApiSlice } from "./slice/account-team.js";
import { personalSecurityApiSlice } from "./slice/personal-security.js";
import { accountSecurityApiSlice } from "./slice/account-security.js";
import { apiKeyApiSlice } from "./slice/api-key.js"; // plan-014 C3
import { domainJoinOfferApiSlice } from "./slice/domain-join-offer.js";
import { billingAccountApiSlice } from "./slice/billing-account.js";
import { billingProfileApiSlice } from "./slice/billing-profile.js";
import { billingOperationApiSlice } from "./slice/billing-operation.js";
import { commercialOfferApiSlice } from "./slice/commercial-offer.js";
import { subscriptionApiSlice } from "./slice/subscription.js";
import { entitlementApiSlice } from "./slice/entitlement.js";
import { financialDocumentApiSlice } from "./slice/financial-document.js";
import { postpaidApiSlice } from "./slice/postpaid.js";
import { resourceShareApiSlice } from "./slice/resource-share.js";
import { lifecycleApiSlice } from "./slice/lifecycle.js"; // plan-014 B4
// plan-015 REG (ED-15): the new customer areas of design §28.4.2, spread once below (QSET's survey-question-set slice
// withdrawn by owner decision 2026-10-09: a campaign asks its agent's live version).
import { documentPreviewApiSlice } from "./slice/document-preview.js";
// plan-017 WAPP-A: the organization's talk rates and its extra-time switch (design §29.9.1, §29.11).
import { talkRateApiSlice } from "./slice/talk-rate.js";
// plan-018 REG: the two new customer areas of design §30.4, spread once below — website-reading (API-AG) and
// handoff (API-HO), each empty until its unit fills it.
import { websiteReadingApiSlice } from "./slice/website-reading.js";
import { handoffApiSlice } from "./slice/handoff.js";
// plan-019 REG: the help area of design §31.4.1–§31.4.2, spread once below, empty until its unit fills it.
import { helpApiSlice } from "./slice/help.js";
import { HelpAccountActionClient } from "../help/account-action-client.js";
// plan-021 REG: the pass-target area of design §33.13.1, spread once below, empty until WAPP-PASS fills it.
import { voiceAgentPassApiSlice } from "./slice/voice-agent-pass.js";
// plan-029 REG: the five areas of design §40.19, spread once below, each empty until its unit fills it.
import { callerProtectionApiSlice } from "./slice/caller-protection.js";
import { blockedNumberApiSlice } from "./slice/blocked-number.js";
import { partyAccessApiSlice } from "./slice/party-access.js";
import { partyConfirmationApiSlice } from "./slice/party-confirmation.js";
import { visitorSigningSecretApiSlice } from "./slice/visitor-signing-secret.js";
import { surveyQuestionnaireApiSlice } from "./slice/survey-questionnaire.js";
// plan-034 WAPP-APPSIGNIN: the consent page's mint of the app authorization code.
import { appSignInApiSlice } from "./slice/app-sign-in.js";
// plan-029 WAPP-HIST1: may_block on the detail, Analytics' refusals and ends by reason.
import { endedByReasonOfWire, mayBlockOfWire, refusedByReasonOfWire } from "./party-protection-view.js";
// plan-029 WAPP-HIST2: the conversation's caller confirmation on the detail.
import { partyConfirmationOfWire } from "./party-confirmation-view.js";
import { analyticsOutboundOfWire } from "./outbound-view.js";
// plan-021 WAPP-HIST: the stints, the acting stint and the pass events on the conversation reads and Analytics.
import {
  actingStintOfWire,
  analyticsVoiceAgentPassOfWire,
  timelineWithVoiceAgentPassOf,
  voiceAgentStintRowOfWire,
  voiceAgentStintsOfWire,
  withVoiceAgentStintOrdinal,
} from "./voice-agent-stint-view.js";
import { sipLegSummaryOfWire } from "./slice/outbound-conversation.js";
import { retryAfterLedger } from "./retry-after.js";
import { withPlainServerFaults } from "./server-fault.js";

/**
 * How the console authenticates. The cookie scheme's CSRF token may be `null` at start-up: a
 * visitor who is not signed in has no auth_session and therefore no token, and one who signs
 * in gets a NEW token at that moment (it is derived from the cookie). So the token is learned
 * from every auth_session body the API returns and forgotten on log-out; a value handed in
 * here is only where it starts.
 */
export type ConsolePersonCredential =
  | ApiPersonCredential
  | { readonly kind: "cookie"; readonly csrfToken: null };

export interface ApiGatewaySettings {
  readonly baseUrl: string;
  readonly personCredential: ConsolePersonCredential;
  readonly fetch: typeof globalThis.fetch;
  /** Injected so a suite can drive the streams without a browser `EventSource`. */
  readonly openStream?: (url: string, onEvent: StreamListener) => StreamSubscription;
  /**
   * Now, for the relative wording on a conversation row and the elapsed time on the live
   * board. Injected so a suite can assert "Today 14:02" without owning the machine's clock.
   */
  readonly now?: () => Date;
  /**
   * Where the public site lives — the Shop view's origin (P12-03/04).
   *
   * Required, with no fallback. The console's "Open shop screen" button builds a link into
   * `apps/web-public`, which is a different origin from the console's own; a guessed origin
   * would produce a link to a page this application does not serve, and a shop owner would
   * find out by putting a tablet on a counter.
   */
  readonly publicSiteBaseUrl: string;
  /** Waits `ms` before a re-read (`currentAuthSession`'s superseded retry). Injected so a suite need not wait. */
  readonly pause?: (ms: number) => Promise<void>;
}

/**
 * plan-014 FIX2: how often, and how far apart, `currentAuthSession` reads again after
 * `409 auth_session_superseded` — another tab's read has just moved the shared cookie to a new token, and this read
 * left before that answer arrived. The API grants the superseded token 30 seconds; by the next read the browser
 * holds the successor. Three tries, 150 ms apart and growing, cover a slow answer without waiting long.
 */
const SUPERSEDED_REREAD_ATTEMPT = 3;
const SUPERSEDED_REREAD_PAUSE_MS = 150;

/**
 * What `X-WeTalk-Csrf` carries before any sign-in has told the console its token.
 *
 * The generated client sends the header on every non-GET cookie request. Before a sign-in
 * there is no auth_session to protect: the API checks the header only against a presented
 * cookie, so the three pre-sign-in calls (discovery, sign-up, log-in) never read it, and any
 * other write sent this value against a live cookie is refused as `csrf_token_invalid`.
 */
const NO_CSRF_TOKEN_YET = "";

export function apiGateway(settings: ApiGatewaySettings): ConsoleGateway {
  // The auth_session's CSRF token, learned from every auth_session body and forgotten on
  // log-out. See {@link ConsolePersonCredential}.
  let csrfToken: string | null =
    settings.personCredential.kind === "cookie" ? settings.personCredential.csrfToken : null;
  const personCredential: ApiPersonCredential =
    settings.personCredential.kind === "bearer"
      ? settings.personCredential
      : validatedDisplayView({
          kind: "cookie",
          // A getter, so the client reads the CURRENT token on every request rather than the
          // one the console happened to boot with.
          get csrfToken(): string { return csrfToken === null ? NO_CSRF_TOKEN_YET : csrfToken; },
        });

  // plan-014 A4 (ED-12): every request carries this tab's scope generation; a late answer from a
  // scope the tab has left is discarded before anything reads it (`../scope/scoped-fetch.ts`).
  const scopeGeneration = createScopeGeneration();
  const scopeAwareFetch = scopedFetch({
    fetch: settings.fetch,
    scope: scopeGeneration,
    baseUrl: settings.baseUrl,
    sendsGeneration: settings.personCredential.kind === "cookie",
  });
  // Notes each 429's Retry-After by request id; the answer itself is untouched (`./retry-after.ts`).
  const retryAfter = retryAfterLedger(scopeAwareFetch);
  const client = new WeTalkApiClient({
    baseUrl: settings.baseUrl,
    // glossary-lint-disable-next-line credential -- ApiClientSettings' own field name
    credential: personCredential,
    // 2026-10-08: a failure on WeTalk's side reaches every screen in plain words, never the
    // server's own sentence (`./server-fault.ts`); status, code and request id are kept.
    fetch: withPlainServerFaults(retryAfter.fetch),
  });
  // plan-025 A-WAPP (design §36.10): a confirmed help confirm card's request — the same fetch, cookie, CSRF token and
  // scope generation as `client`, and only the registry's requests (`../help/account-action-client.ts`).
  const helpActionClient = new HelpAccountActionClient({
    baseUrl: settings.baseUrl,
    // glossary-lint-disable-next-line credential -- ApiClientSettings' own field name
    credential: personCredential,
    fetch: withPlainServerFaults(retryAfter.fetch),
  });
  const open = settings.openStream ?? openEventStream;
  const now = settings.now ?? ((): Date => new Date());
  const pause =
    settings.pause ??
    ((ms: number): Promise<void> =>
      new Promise((resolve) => {
        setTimeout(resolve, ms);
      }));

  // plan-014 A4: an EventSource cannot carry the scope generation, so every stream is tracked and
  // closed the moment this tab switches or learns the scope moved (`./stream.ts`).
  const streams = streamRegistry();
  scopeGeneration.onChange((event) => {
    if (event.kind === "left" || event.previous !== null) streams.closeAll();
  });
  const stream = (
    operationId: string,
    pathParameter: Readonly<Record<string, string>>,
    onEvent: StreamListener,
  ): StreamSubscription =>
    streams.track(open(streamUrlOf(settings.baseUrl, operationId, pathParameter), onEvent));


  /**
   * The live balance. A local function rather than a method, so `getBillingState` can reuse
   * it without reaching for `this` inside an object literal.
   */
  const readCreditBalance = async (): Promise<CreditBalanceView> => {
    const data = readRecord(
      readRecord(assertOk(await client.getBillingBalance()), "getBillingBalance")["data"],
      "getBillingBalance.data",
    );
    const currency = readText(data["currency"], "billingBalance.currency");
    return {
      settled: minorMoney(data, "balance_minor", currency),
      unbilledToday: minorMoney(data, "unbilled_today_minor", currency),
      reserved: minorMoney(data, "reserved_minor", currency),
      available: minorMoney(data, "available_minor", currency),
      heldConversationCount: readWholeNumber(
        data["held_conversation_count"],
        "billingBalance.held_conversation_count",
      ),
      hasTrialGrant: readBoolean(data["has_trial_grant"], "billingBalance.has_trial_grant"),
      refusal: readBillingRefusal(data["refusal"], currency),
      trialSecondsRemaining: readWholeNumber(
        data["trial_second_remaining"],
        "billingBalance.trial_second_remaining",
      ),
      // plan-017 WAPP-A (owner answer OD-C): the rate the low-balance line divides by is the organization's HIGHEST
      // rate in force. The wire's `talk_rate_minor_per_minute` is deprecated (PQ-8, one kind only) and is not read.
      talkRatePerMinute: minorMoney(data, "highest_talk_rate_minor_per_minute", currency),
      lowBalanceTalkMinute: readWholeNumber(data["low_balance_talk_minute"], "billingBalance.low_balance_talk_minute"),
      hardStopEnabled: readBoolean(data["hard_stop_enabled"], "billingBalance.hard_stop_enabled"),
      state: readCreditState(data["state"]),
      workspaceBudget: readWorkspaceBudget(data["workspace_budget"]),
      billingEmail: readText(data["billing_email"], "billingBalance.billing_email"),
    };
  };

  const readChargeAttempt = async (): Promise<readonly ChargeAttemptView[]> => {
    const at = now();
    return readDataArray(
      assertOk(await client.listChargeAttempts({ query: {} })),
      "listChargeAttempts",
    ).map((one, index): ChargeAttemptView => {
      const each = readRecord(one, `chargeAttempt[${String(index)}]`);
      const currency = readText(each["currency"], "chargeAttempt.currency");
      return validatedDisplayView({
        chargeAttemptId: readText(each["charge_attempt_id"], "chargeAttempt.charge_attempt_id"),
        kind: readChargeKind(each["kind"]),
        amount: minorMoney(each, "amount_minor", currency),
        state: readChargeState(each["state"]),
        declineCode: readNullableText(each["decline_code"], "chargeAttempt.decline_code"),
        declineMessage: readNullableText(each["decline_message"], "chargeAttempt.decline_message"),
        attemptCount: readWholeNumber(each["attempt_count"], "chargeAttempt.attempt_count"),
        get whenText() { return formatWhen(readText(each["created_at"], "chargeAttempt.created_at"), at); },
      });
    });
  };

  const memberOfWire = (
    one: Readonly<Record<string, unknown>>,
    at: Date,
  ): WorkspaceMemberView => validatedDisplayView({
    membershipId: readText(one["membership_id"], "member.membership_id"),
    personId: readText(one["person_id"], "member.person_id"),
    // Null is a real state: a membership can outlive the person row it points at, and the
    // two tables are behind different database roles so there is no join that would notice.
    displayName: readNullableText(one["display_name"], "member.display_name"),
    emailAddress: readNullableText(one["email"], "member.email"),
    emailVerified: readBoolean(one["email_verified"], "member.email_verified"),
    role: readMemberRole(one["role"]),
    get joinedAtText() { return nullableWhen(one["joined_at"], "member.joined_at", at); },
    get lastActiveText() { return nullableWhen(one["last_active_at"], "member.last_active_at", at); },
  });

  const invitationOfWire = (
    one: Readonly<Record<string, unknown>>,
    at: Date,
  ): WorkspaceInvitationView => (validatedDisplayView({
    invitationId: readText(one["invitation_id"], "invitation.invitation_id"),
    emailAddress: readText(one["email"], "invitation.email"),
    role: readMemberRole(one["role"]),
    get expiresAtText() { return formatWhen(readText(one["expires_at"], "invitation.expires_at"), at); },
    state: readInvitationState(one["state"]),
  }));

  const apiKeyOfWire = (one: Readonly<Record<string, unknown>>, at: Date): ApiKeyView => (validatedDisplayView({
    apiKeyId: readText(one["api_key_id"], "apiKey.api_key_id"),
    name: readText(one["name"], "apiKey.name"),
    keyPrefix: readText(one["key_prefix"], "apiKey.key_prefix"),
    scope: readTextArray(one["scope"], "apiKey.scope"),
    rateLimitPerMinute: readWholeNumber(
      one["rate_limit_per_minute"],
      "apiKey.rate_limit_per_minute",
    ),
    get lastUsedText() { return nullableWhen(one["last_used_at"], "apiKey.last_used_at", at); },
    get createdText() { return formatWhen(readText(one["created_at"], "apiKey.created_at"), at); },
    createdByName: readNullableText(one["created_by_name"], "apiKey.created_by_name"),
    revoked: one["revoked_at"] !== null,
  }));

  /**
   * An auth_session body (`AuthSessionView` on the wire) as the console's view, in whichever of the
   * three contexts it is (plan-014 A2/A4, design §27.6.1).
   *
   * The CSRF token and the scope generation are learned FIRST, before anything below can raise: an
   * unscoped sign-in is still a sign-in, log-out must be able to end it, and the workspace listing
   * read next must carry the new generation.
   *
   * The person, account, workspace id and role come from the auth_session, and nothing else is
   * read. In a workspace context the workspace's name, member count and month's spend are tenant
   * facts the identity surface does not carry; they used to be read from the workspace listing here,
   * so a failed listing failed the sign-in and took the whole console with it (customer-portal
   * review #150). The shell reads the listing on its own (`shell/current-workspace.ts`). An
   * account-only context names no workspace at all (FR12-AUTH-09: never a dummy workspace).
   */
  const authContextViewOf = async (data: Readonly<Record<string, unknown>>): Promise<AuthSessionView> => {
    csrfToken = readText(data["csrf_token"], "authSession.csrf_token");
    const context = readRecord(data["context"], "authSession.context");
    const generation = context["scope_generation"];
    if (!isScopeGeneration(generation)) {
      throw new MalformedResponseError("authSession.context.scope_generation", "a positive whole number", generation);
    }
    scopeGeneration.adopt(generation);

    const actor = actorViewOf(readRecord(data["actor"], "authSession.actor"));
    const person = readRecord(data["person"], "authSession.person");
    const displayName = readText(person["display_name"], "authSession.person.display_name");
    const base = {
      ...authSessionNoticeOf(data["notice"]),
      person: {
        personId: readText(person["person_id"], "authSession.person.person_id"),
        displayName,
        emailAddress: readText(person["email"], "authSession.person.email"),
        initials: initialsOfName(displayName),
        emailVerified: readBoolean(person["email_verified"], "authSession.person.email_verified"),
      },
      actor,
      scopeGeneration: generation,
      accountChoice: readArray(data["account_choice"], "authSession.account_choice").map((one, index) =>
        accountChoiceOfWire(readRecord(one, `authSession.account_choice[${String(index)}]`)),
      ),
      landing: readLanding(data["landing"]),
      capability: capabilityOfWire(readRecord(context["capability"], "authSession.context.capability")),
    };

    const kind = readText(context["kind"], "authSession.context.kind");
    if (kind === "selection_required") return { kind, ...base };
    if (kind !== "account_only" && kind !== "workspace_selected") {
      throw new MalformedResponseError(
        "authSession.context.kind",
        "selection_required | account_only | workspace_selected",
        kind,
      );
    }
    const account = readRecord(context["account"], "authSession.context.account");
    const accountId = readText(account["account_id"], "authSession.context.account.account_id");
    const accountName = readText(account["name"], "authSession.context.account.name");
    const accountState = readAccountState(account["state"], "authSession.context.account.state");
    const accountRole = readAccountRole(account["account_role"], "authSession.context.account.account_role");
    if (kind === "account_only") {
      if (accountRole === null) {
        throw new MalformedResponseError(
          "authSession.context.account.account_role",
          "an organization role (an organization is entered without a workspace only through one)",
          null,
        );
      }
      return { kind, ...base, accountId, accountName, accountState, accountRole };
    }

    const scoped = readRecord(context["workspace"], "authSession.context.workspace");
    const workspaceId = readText(scoped["workspace_id"], "authSession.context.workspace.workspace_id");
    return {
      kind,
      ...base,
      accountId,
      accountName,
      accountState,
      accountRole,
      workspaceId,
      memberRole: readWorkspaceRole(scoped["member_role"], "authSession.context.workspace.member_role"),
    };
  };

  /**
   * The same, for the entry points that OPEN a sign-in (log in, sign up, creating an organization,
   * the legacy switch, accepting an invitation). An unscoped answer still raises
   * {@link AuthSessionUnscopedError} with its choices — the entry screens offer them — after the
   * CSRF token and the generation are learned.
   */
  const authSessionViewOf = async (data: Readonly<Record<string, unknown>>): Promise<AuthSessionView> => {
    const opened = await authContextViewOf(data);
    if (opened.kind !== "selection_required") return opened;
    throw new AuthSessionUnscopedError(
      readArray(data["account_choice"], "authSession.account_choice").map((one, index) => {
        const each = readRecord(one, `authSession.account_choice[${String(index)}]`);
        return {
          accountId: readText(each["account_id"], "accountChoice.account_id"),
          accountName: readText(each["account_name"], "accountChoice.account_name"),
          role: each["member_role"] === null ? null : readWorkspaceRole(each["member_role"], "accountChoice.member_role"),
        };
      }),
    );
  };

  /** `POST /v1/session/account` under the current generation (plan-014 A2's contract). */
  const switchScopeTo = async (target: ScopeTargetView): Promise<SwitchedScopeView> => {
    // A tab that has not read its auth_session yet learns the generation first: the switch refuses
    // a missing one, and guessing a number would be the stale-tab bug this exists to prevent.
    let generation = scopeGeneration.current();
    if (generation === null) {
      const read = await client.getCurrentAuthSession();
      generation = (await authContextViewOf(authSessionDataOf(assertOk(read), "getCurrentAuthSession"))).scopeGeneration;
    }
    const switched = await client.switchAuthSessionAccount({
      body: { account_id: target.accountId, workspace_id: target.workspaceId, scope_generation: generation },
    });
    const data = authSessionDataOf(assertOk(switched), "switchAuthSessionAccount");
    return {
      authSession: await authContextViewOf(data),
      previousAccountName: readNullableText(data["previous_account_name"], "authSession.previous_account_name"),
    };
  };

  /**
   * Who is acting (plan-002 RB-18, DD-7). An operator's banner needs the name and the reason the
   * API reads from the customer's own `audit_entry`; a body without them still raises
   * `ImpersonationDetailUnavailableError` rather than inventing who is acting.
   */
  const actorViewOf = (actor: Readonly<Record<string, unknown>>): AuthSessionActorView => {
    const kind = readText(actor["kind"], "authSession.actor.kind");
    if (kind === "self") return { kind: "self" };
    if (
      kind !== "operator" ||
      typeof actor["operator_display_name"] !== "string" ||
      typeof actor["reason"] !== "string"
    ) {
      throw new ImpersonationDetailUnavailableError();
    }
    return {
      kind: "operator",
      operatorDisplayName: readText(actor["operator_display_name"], "authSession.actor.operator_display_name"),
      reason: readText(actor["reason"], "authSession.actor.reason"),
      expiresAt: readText(actor["expires_at"], "authSession.actor.expires_at"),
    };
  };

  const authSessionDataOf = (body: unknown, operationId: string): Readonly<Record<string, unknown>> =>
    readRecord(readRecord(body, operationId)["data"], `${operationId}.data`);

  /** What every plan-002 slice is built with (IC-2). The same clock and the same session. */
  const sliceContext: SliceContext = {
    now,
    formatWhen,
    publicSiteBaseUrl: settings.publicSiteBaseUrl,
    adoptAuthSession: (body, operationId) => authSessionViewOf(authSessionDataOf(body, operationId)),
    takeRetryAfterSecond: retryAfter.take,
    sendHelpAccountAction: (request, helpActionId) => helpActionClient.act(request, helpActionId),
  };

  return {
    // --- identity (P5-01, P5-05) -------------------------------------------------------------
    async discoverIdentifier(emailAddress: string, guard?: EntryGuardView): Promise<DiscoveryOutcome> {
      const data = readRecord(
        readRecord(
          assertOk(
            await client.discoverIdentity({ body: { email: emailAddress, ...entryGuardBodyOf(await entryGuardOrSolved(guard, client)) } }),
          ),
          "discoverIdentity",
        )["data"],
        "discoverIdentity.data",
      );
      const method = readText(data["method"], "discovery.method");
      if (method !== "password" && method !== "microsoft" && method !== "google") {
        throw new MalformedResponseError("discovery.method", "password | microsoft | google", method);
      }
      return {
        method,
        startUrl: readNullableText(data["start_url"], "discovery.start_url"),
        showPassword: readBoolean(data["show_password"], "discovery.show_password"),
      };
    },

    async readIdentityChallenge(): Promise<unknown> {
      return assertOk(await client.createIdentityChallenge());
    },

    // `signUp` is in the account slice (RB-17's), spread below.

    async logIn(request: LogInRequestView): Promise<AuthSessionView> {
      const answered = await client.logInPerson({
        body: {
          email: request.emailAddress,
          password: request.password,
          // ADR 0071: a caller that passes no guard (the invitation pages' "Log in") still sends a solved challenge.
          ...entryGuardBodyOf(await entryGuardOrSolved(request.guard, client)),
        },
      });
      const data = authSessionDataOf(assertOk(answered), "logInPerson");
      const opened = await authContextViewOf(data);
      // Design §27.6.3: a person with exactly ONE organization is opened in it, after this fresh
      // access read — in its workspace, or in the organization alone when they hold only an
      // organization role there (Finance). Several, or none, are the entry screen's to offer.
      const [only, ...rest] = opened.accountChoice;
      if (opened.kind === "selection_required" && only !== undefined && rest.length === 0) {
        const target = defaultScopeTargetOf(only, null);
        if (target !== null) return (await switchScopeTo(target)).authSession;
      }
      return authSessionViewOf(data);
    },

    async currentAuthSession(): Promise<AuthSessionView | null> {
      let answered = await client.getCurrentAuthSession();
      // plan-014 FIX2: another tab's read moved the shared cookie to a new token a moment ago, and this read carried
      // the old one. The API left the cookie alone; reading again sends the one the browser now holds. Never a
      // sign-out: once the tries are spent the answer is raised as the fault it is.
      for (let attempt = 1; attempt <= SUPERSEDED_REREAD_ATTEMPT && isSuperseded(answered); attempt += 1) {
        await pause(SUPERSEDED_REREAD_PAUSE_MS * attempt);
        answered = await client.getCurrentAuthSession();
      }
      // 401 is the ordinary answer for "nobody is signed in here" (no cookie, or one that is
      // expired or revoked — the API clears it). Anything else that is not a 2xx is a fault.
      if (answered.status === 401) {
        csrfToken = null;
        scopeGeneration.forget();
        streams.closeAll();
        return null;
      }
      return authContextViewOf(authSessionDataOf(assertOk(answered), "getCurrentAuthSession"));
    },

    switchScope: switchScopeTo,
    scopeGeneration,

    async logOut(): Promise<void> {
      const answered = await client.logOutPerson({});
      // `authentication_required` means there was no auth_session on the request to end —
      // the outcome the caller wanted. Every other 401 (a CSRF refusal above all) is NOT
      // "signed out": the session is still live, and saying otherwise would be a lie.
      if (
        answered.status === 401 &&
        new ApiError(answered.status, answered.body, answered.requestId).code ===
          "authentication_required"
      ) {
        csrfToken = null;
        scopeGeneration.forget();
        streams.closeAll();
        return;
      }
      assertOk(answered);
      csrfToken = null;
      scopeGeneration.forget();
      streams.closeAll();
    },

    googleStartUrl(returnPath: string): string {
      // Not a fetch and not an operation: it is where the browser is sent. The path is the
      // one design §8.4 fixes for the relying-party start, and it is built from the
      // configured API base URL rather than assumed to be same-origin.
      const target = new URL("/v1/auth/google/start", settings.baseUrl);
      target.searchParams.set("return", returnPath);
      return target.toString();
    },

    apiReferenceUrl(): string {
      // Review item 188: the reference lives on the public site, a different origin, so it is
      // built from the configured public-site base rather than assumed to be this console's.
      return new URL("/docs/api/", settings.publicSiteBaseUrl).toString();
    },

    publicSiteUrl(path: string): string {
      return new URL(path, settings.publicSiteBaseUrl).toString();
    },

    // --- shell -------------------------------------------------------------------------------
    // Notifications and first-run progress are in their slices, spread below.

    /**
     * The banner's four states, from two reads.
     *
     * `healthy | low | out-of-credit` come from the live balance; **`declined` cannot**, because
     * a decline is a fact about a card and `creditStateOf` only knows about money. So the most
     * recent charge attempt is read as well, and the two are combined here rather than being
     * collapsed into one server-side word that could not distinguish them.
     */
    async getBillingState(): Promise<BillingStateView> {
      const [credit, attempt] = await Promise.all([readCreditBalance(), readChargeAttempt()]);
      const latest = attempt[0];
      const declined = latest?.state === "declined" || latest?.state === "requires_reauth";
      const exhausted = credit.state === "exhausted" || credit.state === "hard_stopped";
      const lowOnTrial = credit.trialSecondsRemaining > 0 && credit.trialSecondsRemaining < 300;
      const lowOnCredit =
        credit.trialSecondsRemaining === 0 &&
        credit.available.amountMinor > 0 &&
        credit.available.amountMinor < credit.talkRatePerMinute.amountMinor * credit.lowBalanceTalkMinute;
      return {
        health: exhausted
          ? "out-of-credit"
          : declined
            ? "declined"
            : lowOnTrial || lowOnCredit
              ? "low"
              : "healthy",
        balance: credit.available,
        trialSecondsRemaining:
          credit.trialSecondsRemaining === 0 ? null : credit.trialSecondsRemaining,
        refusal: credit.refusal,
      };
    },

    // --- catalogue: documented -------------------------------------------------------------
    // plan-015 INT-B: the forward facts `GET /v1/templates` answers beside its templates.
    async readForwardFact() {
      return forwardFactOfWire(readRecord(assertOk(await client.listAgentTemplates()), "listAgentTemplates"), "listAgentTemplates");
    },

    async listAgentTemplates(): Promise<readonly AgentTemplateCardView[]> {
      const rows = readDataArray(assertOk(await client.listAgentTemplates()), "listAgentTemplates");
      const availableCode = new Set(
        rows.map((row, index) => readText(readRecord(row, "template")["code"], `template[${String(index)}].code`)),
      );
      // The content-step blurb is the API's copy for every template the API returns: a migration may
      // correct it (0034 made the delivery blurb business-neutral, customer-portal review #6), while
      // the compiled catalogue stays pinned to 0006 by its drift gate.
      const wireContentDek = new Map(
        rows.map((row, index) => {
          const one = readRecord(row, `template[${String(index)}]`);
          return [
            readText(one["code"], `template[${String(index)}].code`),
            readText(one["content_dek"], `template[${String(index)}].content_dek`),
          ] as const;
        }),
      );

      // Who starts the conversation is the API's for every template it returns (plan-015 G7, 0118); the coming-soon
      // rows it cannot return state theirs in the compiled catalogue, from the same publication.
      const wireDirection = new Map(
        rows.map((row, index) => {
          const one = readRecord(row, `template[${String(index)}]`);
          return [
            readText(one["code"], `template[${String(index)}].code`),
            directionOfWire(one["direction"], `template[${String(index)}].direction`),
          ] as const;
        }),
      );

      // The six-row shape comes from the catalogue the migration seeds and `@wetalk/domain`
      // compiles in; the API's list decides which of them is buildable today. A row the API
      // returns that the compiled catalogue does not know about is built from the wire.
      const known = AGENT_TEMPLATE_CATALOGUE.map(
        (one): AgentTemplateCardView => ({
          code: one.code,
          name: one.name,
          dek: one.dek,
          youAdd: one.youAdd,
          iconName: one.icon,
          available: availableCode.has(one.code),
          contentTitle: one.contentTitle,
          contentDek: wireContentDek.get(one.code) ?? one.contentDek,
          fieldSet: one.fieldSet,
          defaultGreeting: one.defaultGreeting,
          defaultName: one.defaultName,
          outcomeVocabulary: one.outcomeVocabulary,
          successMetricLabel: one.successMetricLabel,
          referenceSchema: one.referenceSchema,
          direction: wireDirection.get(one.code) ?? one.direction,
        }),
      );

      const knownCode = new Set(known.map((one) => one.code));
      const extra = rows
        .map((row, index) => cardOfWire(readRecord(row, `template[${String(index)}]`)))
        .filter((one) => !knownCode.has(one.code));

      return [...known, ...extra].map(localizeAgentTemplateCard);
    },

    async listLanguagePacks(): Promise<readonly LanguagePackView[]> {
      return readDataArray(assertOk(await client.listLanguagePacks()), "listLanguagePacks").map(
        (row, index): LanguagePackView => {
          const one = readRecord(row, `languagePack[${String(index)}]`);
          return {
            code: readText(one["code"], "languagePack.code"),
            name: readText(one["name"], "languagePack.name"),
            nativeName: readText(one["native_name"], "languagePack.native_name"),
            maturity: languageMaturityOfWire(one["maturity"], "languagePack.maturity"),
            defaultVoiceId: readNullableText(one["default_voice_id"], "languagePack.default_voice_id"),
          };
        },
      );
    },

    async listVoices(): Promise<readonly VoiceView[]> {
      return readDataArray(assertOk(await client.listVoices()), "listVoices").map(
        (row, index): VoiceView => {
          const one = readRecord(row, `voice[${String(index)}]`);
          return validatedDisplayView({
            voiceId: readText(one["voice_id"], "voice.voice_id"),
            get name() { return voiceCatalogueText(readText(one["name"], "voice.name")); },
            get description() { return voiceCatalogueText(readText(one["description"], "voice.description")); },
            supportedLanguageCode: readTextArray(
              one["supported_language_code"],
              "voice.supported_language_code",
            ),
            gender: voiceGenderOfWire(one["gender"], "voice.gender"),
          });
        },
      );
    },

    async listTiers(): Promise<readonly TierView[]> {
      return readDataArray(assertOk(await client.listTiers()), "listTiers").map(
        (row, index): TierView => {
          const one = readRecord(row, `tier[${String(index)}]`);
          return {
            tierId: readText(one["tier_id"], "tier.tier_id"),
            code: readText(one["code"], "tier.code"),
            name: readText(one["name"], "tier.name"),
            channelUnitCeiling: readWholeNumber(
              one["channel_unit_ceiling"],
              "tier.channel_unit_ceiling",
            ),
            ordinal: readWholeNumber(one["ordinal"], "tier.ordinal"),
          };
        },
      );
    },

    async getNumberAvailability(): Promise<NumberAvailabilityView> {
      // The answer is in the `data` envelope like every other read. Reading the top level had
      // been wrong since P10 and went unseen until the new-agent wizard read it (2026-09-26:
      // every new agent failed at step 2 with "numberAvailability.available … undefined").
      const body = readRecord(
        readRecord(assertOk(await client.getNumberAvailability()), "getNumberAvailability")["data"],
        "getNumberAvailability.data",
      );
      return {
        available: readBoolean(body["available"], "numberAvailability.available"),
        reason: readText(body["reason"], "numberAvailability.reason"),
        detail: readText(body["detail"], "numberAvailability.detail"),
      };
    },

    // --- the VoiceAgent: documented since P11-01 ----------------------------------------------
    async createVoiceAgent(draft: VoiceAgentDraft): Promise<CreatedVoiceAgentView> {
      const body = readRecord(
        assertOk(
          await client.createVoiceAgent({
            body: {
              agent_template_code: draft.agentTemplateCode,
              name: draft.displayName,
              field_value: draft.fieldValue,
              language_code: [...draft.languageCode],
              ...createVoiceAgentBodyVoiceOf(draft),
              greeting: draft.greeting,
              number_choice: draft.numberChoice,
              medium: [...draft.medium],
              channel_unit_count: draft.channelUnitCount,
              ...(draft.recordingEnabled === undefined ? {} : { recording_enabled: draft.recordingEnabled }),
              ...(draft.paymentMethod === undefined || draft.paymentMethod === null
                ? {}
                : { payment_method: [...draft.paymentMethod] }),
              ...(draft.resourceShareId === undefined ? {} : { resource_share_id: draft.resourceShareId }),
              // plan-015 AGC-UI: the wizard's time zone, special dates and out-of-hours / busy choices (IC-DG-2).
              ...answeringBodyOf(draft.answering),
            },
          }),
        ),
        "createVoiceAgent",
      );
      const data = readRecord(body["data"], "createVoiceAgent.data");
      return {
        voiceAgentId: readText(data["voice_agent_id"], "createVoiceAgent.voice_agent_id"),
        buildJobId: readText(data["build_job_id"], "createVoiceAgent.build_job_id"),
      };
    },

    async listVoiceAgents(): Promise<readonly VoiceAgentView[]> {
      return readDataArray(assertOk(await client.listVoiceAgents()), "listVoiceAgents").map(
        // plan-015 LST (G15): the list carries `campaign_dialling` ("Calling").
        (row, index) => voiceAgentListItemOfWire(row, `voiceAgent[${String(index)}]`),
      );
    },

    async getVoiceAgent(voiceAgentId: string): Promise<VoiceAgentView> {
      const body = readRecord(
        assertOk(await client.getVoiceAgent({ path: { voice_agent_id: voiceAgentId } })),
        "getVoiceAgent",
      );
      return voiceAgentOfWire(body["data"], "voiceAgent");
    },

    // --- conversations: documented (defect D4) -----------------------------------------------
    async getLiveSnapshot(voiceAgentId: string): Promise<LiveSnapshotView> {
      const body = readRecord(
        assertOk(await client.listLiveConversations({ query: { voice_agent_id: voiceAgentId } })),
        "listLiveConversations",
      );
      const data = readRecord(body["data"], "listLiveConversations.data");
      const at = now();

      const conversation = readArray(
        data["conversation"],
        "liveSnapshot.conversation",
      ).map((row, index): LiveBoardConversationView => {
        const one = readRecord(row, `liveConversation[${String(index)}]`);
        const startedAt = readText(one["started_at"], "liveConversation.started_at");
        return {
          conversationId: readText(one["conversation_id"], "liveConversation.conversation_id"),
          partyAddress: readNullableText(one["party_address"], "liveConversation.party_address"),
          medium: readMedium(one["medium"], "liveConversation.medium"),
          languageCode: readNullableText(one["language_code"], "liveConversation.language_code"),
          phase: phaseOf(
            readNullableText(one["last_event_kind"], "liveConversation.last_event_kind"),
          ),
          elapsedSeconds: elapsedSecondsOf(startedAt, at),
          // plan-015 LIVE (G14): what the ⓘ shows — the leg's observed format and its latency so far, or null.
          ...liveBoardFactOfWire(one),
          // plan-021: the VoiceAgent answering it now ("Now with {name}" when another one is).
          actingStint: actingStintOfWire(one["acting_stint"], "liveConversation.acting_stint"),
          // WAPP-ENDCALL: an outbound conversation's origin — where "End call" is offered.
          outboundOrigin: readOutboundOrigin(one, "liveConversation.outbound_origin"),
        };
      });

      const billedSecondToday = readWholeNumber(
        data["billed_second_today"],
        "liveSnapshot.billed_second_today",
      );

      return {
        liveConversationCount: readWholeNumber(
          data["live_conversation_count"],
          "liveSnapshot.live_conversation_count",
        ),
        // The API answers in seconds, which is what the meter counts, and the screen shows
        // minutes and seconds (`talkTimeText`) — so the seconds travel unrounded.
        talkSecondToday: billedSecondToday,
        spendToday: readMoney(data["billed_amount_today"], "liveSnapshot.billed_amount_today"),
        priceUnavailableCountToday: readWholeNumber(
          data["price_unavailable_count_today"],
          "liveSnapshot.price_unavailable_count_today",
        ),
        outcomeCountToday: readWholeNumber(
          data["outcome_count_today"],
          "liveSnapshot.outcome_count_today",
        ),
        outcomeLabel: readText(data["outcome_label"], "liveSnapshot.outcome_label"),
        meter: validatedDisplayView({
          inUse: readWholeNumber(data["channel_unit_in_use"], "liveSnapshot.channel_unit_in_use"),
          ceiling: readWholeNumber(
            data["channel_unit_ceiling"],
            "liveSnapshot.channel_unit_ceiling",
          ),
          get note() { return portalText("shell", "calls.queueLimitNote"); },
        }),
        conversation,
      };
    },

    async listPastConversations(query: HistoryQuery): Promise<PastConversationPageView> {
      const at = now();
      const body = readRecord(
        assertOk(
          await client.listConversations({
            query: historyQueryOf(query, at),
          }),
        ),
        "listConversations",
      );

      const row = readArray(body["data"], "listConversations.data").map(
        (one, index): PastConversationRowView =>
          // plan-021: who answered first, and how many VoiceAgents acted in it.
          extendDisplayView(
            rowOfWire(one, `conversation[${String(index)}]`, at),
            voiceAgentStintRowOfWire(readRecord(one, `conversation[${String(index)}]`), `conversation[${String(index)}]`),
          ),
      );

      const figure = readRecord(body["figure"], "listConversations.figure");
      const billedSecondTotal = readWholeNumber(
        figure["billed_second_total"],
        "figure.billed_second_total",
      );
      const conversationCount = readWholeNumber(
        figure["conversation_count"],
        "figure.conversation_count",
      );
      const answeredCount = readWholeNumber(figure["answered_count"], "figure.answered_count");

      const stat: readonly HistoryStatView[] = historyStatOf({
        conversationCount,
        answeredCount,
        billedSecondTotal,
        billedAmount: readMoney(figure["billed_amount_total"], "figure.billed_amount_total"),
        listAmount: readMoney(figure["list_amount_total"], "figure.list_amount_total"),
        chargedSecondTotal: readWholeNumber(figure["charged_second_total"], "figure.charged_second_total"),
        priceUnavailableCount: readWholeNumber(figure["price_unavailable_count"], "figure.price_unavailable_count"),
      });
      const outcomeOption = readArray(body["outcome_option"], "listConversations.outcome_option").map(
        (one, index) => readText(one, `listConversations.outcome_option[${String(index)}]`),
      );

      const channelLabelOption = channelLabelOptionOfWire(
        body["channel_label_option"],
        "listConversations.channel_label_option",
      );

      // plan-021: the listed conversations passed in from another VoiceAgent (in no other figure).
      const passedInConversationCount = readWholeNumber(
        figure["passed_in_conversation_count"],
        "figure.passed_in_conversation_count",
      );

      return { row, stat, outcomeOption, channelLabelOption, passedInConversationCount };
    },

    async getPastConversation(conversationId: string): Promise<PastConversationView> {
      const at = now();
      const body = readRecord(
        assertOk(await client.getConversation({ path: { conversation_id: conversationId } })),
        "getConversation",
      );
      const data = readRecord(body["data"], "getConversation.data");
      const transcript = data["transcript"];
      const customerOrder = customerOrderOfWire(data["customer_order"], "conversation.customer_order");
      // plan-005 OP-44: a mixed conversation's parts and per-medium charge (absent from an older api).
      const segment = segmentOfWire(data, "conversation");
      const mediumSplit = mediumSplitOfWire(data, "conversation");
      // plan-008 ST-AN/ST-UA: a conversation through the account's own SIP trunk (absent for any other leg).
      const sipLeg = data["sip_leg"];
      // plan-018 API-MSG: the messages a caller left and, when the caller asked for a person, how that went.
      const takenMessage = takenMessageListOfWire(data["taken_message"], "conversation.taken_message");
      // plan-021: the VoiceAgents that answered it, each line's and each event's stint, and its pass events.
      const voiceAgentStint = voiceAgentStintsOfWire(data["voice_agent_stint"], "conversation.voice_agent_stint");
      const timeline = timelineWithVoiceAgentPassOf(data["event"], "conversation.event", at, voiceAgentStint);

      return validatedDisplayView(extendDisplayView(rowOfWire(data, "conversation", at), {
        ...voiceAgentStintRowOfWire(data, "conversation"),
        voiceAgentStint,
        voiceAgentPass: timeline.voiceAgentPass,
        get summary() { return transcript === null
            ? null
            : readNullableText(
                readRecord(transcript, "conversation.transcript")["summary"],
                "conversation.transcript.summary",
              ); },
        transcript: withVoiceAgentStintOrdinal(transcriptOfWire(data["turn"], "conversation.turn"), data["turn"], "conversation.turn"),
        event: timeline.event,
        recording: recordingOfWire(data["recording"], "conversation.recording"),
        // Design §8.8: playing a recording is an audited act with its own route. Until that
        // route exists there is no URL to hand the player, and the panel shows the reference
        // instead of a control that cannot work.
        recordingUrl: null,
        // Review #113: the order this conversation placed, read from `customer_order` by the API.
        outcomeValue: customerOrder === null ? null : customerOrder.total,
        customerOrder,
        transcriptWithheld: readBoolean(data["transcript_withheld"], "conversation.transcript_withheld"),
        // plan-029 API-READ1: whether "Block this number" is offered.
        mayBlock: mayBlockOfWire(data, "conversation"),
        // plan-029 API-CONF (WAPP-HIST2): how the caller confirmed who they were, or that it locked.
        partyConfirmation: partyConfirmationOfWire(data, "conversation"),
        ...(segment === undefined ? {} : { segment }),
        ...(mediumSplit === undefined ? {} : { mediumSplit }),
        ...(sipLeg === undefined ? {} : { sipLeg: sipLeg === null ? null : sipLegSummaryOfWire(sipLeg, "conversation.sip_leg") }),
        ...(takenMessage === undefined
          ? {}
          : { takenMessage, handoff: conversationHandoffOfWire(data["handoff"], "conversation.handoff") }),
      }));
    },

    // --- Agent · Channels, Numbers & SIP, Messaging: all documented (P11-04/05/06) --------
    async listVoiceAgentChannelBindings(voiceAgentId): Promise<readonly ChannelBindingRowView[]> {
      const rows = readDataArray(
        assertOk(await client.listVoiceAgentChannelBindings({ path: { voice_agent_id: voiceAgentId } })),
        "listVoiceAgentChannelBindings",
      );
      const at = now();
      return rows.map((row, index) =>
        channelBindingOfWire(row, `channelBinding[${String(index)}]`, at),
      );
    },

    async getVoiceAgentConcurrency(voiceAgentId): Promise<ConcurrencySummaryView> {
      const body = readRecord(
        assertOk(await client.getVoiceAgentConcurrency({ path: { voice_agent_id: voiceAgentId } })),
        "getVoiceAgentConcurrency",
      );
      return concurrencyOfWire(body["data"], "concurrency");
    },

    async getVoiceAgentConnection(voiceAgentId): Promise<VoiceAgentConnectionView> {
      const body = readRecord(
        assertOk(await client.getVoiceAgentConnection({ path: { voice_agent_id: voiceAgentId } })),
        "getVoiceAgentConnection",
      );
      return connectionOfWire(body["data"], "connection", now());
    },

    async getVoiceAgentAnswering(voiceAgentId): Promise<AnsweringScheduleView> {
      const body = readRecord(
        assertOk(await client.getVoiceAgentAnswering({ path: { voice_agent_id: voiceAgentId } })),
        "getVoiceAgentAnswering",
      );
      return answeringOfWire(body["data"], "answering");
    },

    async listVoiceAgentSignallingEvents(voiceAgentId): Promise<readonly SignallingEventView[]> {
      const rows = readDataArray(
        assertOk(
          await client.listVoiceAgentSignallingEvents({ path: { voice_agent_id: voiceAgentId } }),
        ),
        "listVoiceAgentSignallingEvents",
      );
      const at = now();
      return rows.map((row, index) => signallingOfWire(row, `signalling[${String(index)}]`, at));
    },

    async requestSipRotation(voiceAgentId): Promise<SipRotationView> {
      const body = readRecord(
        assertOk(await client.requestSipRotation({ path: { voice_agent_id: voiceAgentId } })),
        "requestSipRotation",
      );
      const data = readRecord(body["data"], "sipRotation");
      return {
        sipCredentialId: readText(data["sip_credential_id"], "sipRotation.sip_credential_id"),
        state: readText(data["state"], "sipRotation.state"),
        sipAddress: readText(data["sip_address"], "sipRotation.sip_address"),
      };
    },

    async listNumbers(): Promise<NumberPageView> {
      return numberPageOfWire(assertOk(await client.listNumbers()), now());
    },

    async readNumberActivity(phoneNumberId: string): Promise<NumberActivityView> {
      return numberActivityOfWire(assertOk(await client.getNumberActivity({ path: { phone_number_id: phoneNumberId } })), now());
    },

    async listSipCredentials(): Promise<readonly WorkspaceSipCredentialView[]> {
      const rows = readDataArray(assertOk(await client.listSipCredentials()), "listSipCredentials");
      const at = now();
      return rows.map((row, index) => workspaceSipOfWire(row, `sipCredential[${String(index)}]`, at));
    },

    async getRegionLatency(): Promise<RegionLatencyView> {
      const body = readRecord(assertOk(await client.getRegionLatency()), "getRegionLatency");
      return regionLatencyOfWire(body["data"], "regionLatency", now());
    },

    async listMessagingMedia(): Promise<readonly MessagingMediumRowView[]> {
      const rows = readDataArray(assertOk(await client.listMessagingMedia()), "listMessagingMedia");
      const at = now();
      return rows.map((row, index) => mediumRowOfWire(row, `medium[${String(index)}]`, at));
    },

    async getMessagingMedium(mediumCode): Promise<MessagingMediumDetailView> {
      const body = readRecord(
        assertOk(await client.getMessagingMedium({ path: { medium_code: mediumCode } })),
        "getMessagingMedium",
      );
      return mediumDetailOfWire(body["data"], "medium", now(), settings.baseUrl);
    },

    async connectMessagingMedium(mediumCode, voiceAgentId): Promise<WebChatWidgetView> {
      const body = readRecord(
        assertOk(
          await client.connectMessagingMedium({
            path: { medium_code: mediumCode },
            body: { voice_agent_id: voiceAgentId },
          }),
        ),
        "connectMessagingMedium",
      );
      const data = readRecord(body["data"], "connection");
      const widget = widgetOfWire(data["widget"], "connection.widget", settings.baseUrl);
      if (widget === null) {
        throw new MalformedResponseError("connection.widget", "a widget", data["widget"]);
      }
      return widget;
    },

    async disconnectMessagingMedium(mediumCode): Promise<void> {
      assertOk(await client.disconnectMessagingMedium({ path: { medium_code: mediumCode } }));
    },

    async listWebChatConversations(): Promise<readonly WebChatConversationRowView[]> {
      const rows = readDataArray(
        assertOk(await client.listMessagingConversations()),
        "listMessagingConversations",
      );
      const at = now();
      return rows.map((row, index) => messagingConversationRowOfWire(row, `conversation[${String(index)}]`, at));
    },

    // --- billing: documented (P10-04, extended by P11-07) -------------------------------------
    getCreditBalance: (): Promise<CreditBalanceView> => readCreditBalance(),

    async listLedgerEntries(cursor: string | null): Promise<LedgerPageView> {
      const body = readRecord(
        assertOk(
          await client.listLedgerEntries({
            query: cursor === null ? {} : { cursor },
          }),
        ),
        "listLedgerEntries",
      );
      const at = now();
      const row = readArray(body["data"], "listLedgerEntries.data").map(
        (one, index): LedgerEntryView => {
          const each = readRecord(one, `ledgerEntry[${String(index)}]`);
          const currency = readText(each["currency"], "ledgerEntry.currency");
          return validatedDisplayView({
            ledgerEntryId: readText(each["ledger_entry_id"], "ledgerEntry.ledger_entry_id"),
            sequence: readWholeNumber(each["sequence"], "ledgerEntry.sequence"),
            get whenText() { return formatWhen(readText(each["occurred_at"], "ledgerEntry.occurred_at"), at); },
            kind: readText(each["kind"], "ledgerEntry.kind"),
            get description() { return readText(each["description"], "ledgerEntry.description"); },
            voiceAgentId: readNullableText(each["voice_agent_id"], "ledgerEntry.voice_agent_id"),
            usageDay: readNullableText(each["usage_day"], "ledgerEntry.usage_day"),
            billedSeconds:
              each["billed_second"] === null
                ? null
                : readWholeNumber(each["billed_second"], "ledgerEntry.billed_second"),
            amount: minorMoney(each, "amount_minor", currency),
            balanceAfter: minorMoney(each, "balance_after_minor", currency),
          });
        },
      );
      return {
        row,
        nextCursor: readNullableText(body["next_cursor"], "listLedgerEntries.next_cursor"),
      };
    },

    async listPaymentMethods(): Promise<readonly PaymentMethodView[]> {
      return readDataArray(
        assertOk(await client.listBillingPaymentMethods()),
        "listBillingPaymentMethods",
      ).map((one, index): PaymentMethodView => {
        const each = readRecord(one, `paymentMethod[${String(index)}]`);
        return {
          paymentMethodId: readText(each["payment_method_id"], "paymentMethod.payment_method_id"),
          brand: readText(each["brand"], "paymentMethod.brand"),
          // The only card digits that exist anywhere in this platform.
          lastFour: readText(each["last_four"], "paymentMethod.last_four"),
          expiryMonth: readWholeNumber(each["expiry_month"], "paymentMethod.expiry_month"),
          expiryYear: readWholeNumber(each["expiry_year"], "paymentMethod.expiry_year"),
          holderName: readText(each["holder_name"], "paymentMethod.holder_name"),
          isDefault: readBoolean(each["is_default"], "paymentMethod.is_default"),
          hasMandate: readBoolean(each["has_mandate"], "paymentMethod.has_mandate"),
        };
      });
    },

    async setDefaultPaymentMethod(paymentMethodId: string): Promise<void> {
      assertOk(
        await client.setBillingDefaultPaymentMethod({
          path: { payment_method_id: paymentMethodId },
        }),
      );
    },

    async removePaymentMethod(paymentMethodId: string): Promise<void> {
      assertOk(
        await client.deleteBillingPaymentMethod({ path: { payment_method_id: paymentMethodId } }),
      );
    },


    async getAutoReload(): Promise<AutoReloadView> {
      return autoReloadOfWire(
        readRecord(assertOk(await client.getAutoReloadPolicy()), "getAutoReloadPolicy")["data"],
      );
    },

    async saveAutoReload(policy): Promise<AutoReloadView> {
      return autoReloadOfWire(
        readRecord(
          assertOk(
            await client.putAutoReloadPolicy({
              // plan-014 review I-9: the revision the screen read, so a stale screen never overwrites a newer policy.
              ...(policy.revision === null ? {} : { header: { "if-match": `"${String(policy.revision)}"` } }),
              body: {
                enabled: policy.enabled,
                threshold_minor: policy.threshold.amountMinor,
                top_up_minor: policy.topUp.amountMinor,
                monthly_cap_minor: policy.monthlyCap.amountMinor,
                payment_method_id: policy.paymentMethodId,
              },
            }),
          ),
          "putAutoReloadPolicy",
        )["data"],
      );
    },

    async getSpendControl(): Promise<SpendControlView> {
      return spendControlOfWire(
        readRecord(assertOk(await client.getBudget()), "getBudget")["data"],
      );
    },

    async saveSpendControl(control): Promise<SpendControlView> {
      return spendControlOfWire(
        readRecord(
          assertOk(
            await client.putBudget({
              body: {
                month_budget_minor: control.monthBudget.amountMinor,
                alert_threshold_percent: control.alertThresholdPercent,
                hard_stop_enabled: control.hardStopEnabled,
              },
            }),
          ),
          "putBudget",
        )["data"],
      );
    },

    async listUsageDays(fromDay: string, toDay: string): Promise<readonly UsageDayView[]> {
      return readDataArray(
        assertOk(await client.listBillingUsage({ query: { from: fromDay, to: toDay } })),
        "listBillingUsage",
      ).map((one, index): UsageDayView => {
        const each = readRecord(one, `usageDaily[${String(index)}]`);
        const currency = readText(each["currency"], "usageDaily.currency");
        return {
          voiceAgentId: readText(each["voice_agent_id"], "usageDaily.voice_agent_id"),
          usageDay: readText(each["usage_day"], "usageDaily.usage_day"),
          medium: readMedium(each["medium"], "usageDaily.medium"),
          isTest: readBoolean(each["is_test"], "usageDaily.is_test"),
          billedSeconds: readWholeNumber(each["billed_second"], "usageDaily.billed_second"),
          conversationCount: readWholeNumber(
            each["conversation_count"],
            "usageDaily.conversation_count",
          ),
          priced: minorMoney(each, "priced_minor", currency),
          // Review #109 and #103: what billing charges, and whether day close has closed it yet.
          charged: minorMoney(each, "charged_minor", currency),
          closed: readBoolean(each["closed"], "usageDaily.closed"),
          today: readBoolean(each["today"], "usageDaily.today"),
        };
      });
    },

    listChargeAttempts: (): Promise<readonly ChargeAttemptView[]> => readChargeAttempt(),

    async listInvoices(): Promise<readonly InvoiceView[]> {
      return readDataArray(
        assertOk(await client.listInvoices({ query: {} })),
        "listInvoices",
      ).map((one, index): InvoiceView => {
        const each = readRecord(one, `invoice[${String(index)}]`);
        const currency = readText(each["currency"], "invoice.currency");
        return {
          invoiceId: readText(each["invoice_id"], "invoice.invoice_id"),
          periodMonth: readText(each["period_month"], "invoice.period_month"),
          minuteTotal: readWholeNumber(each["minute_total"], "invoice.minute_total"),
          amount: minorMoney(each, "amount_minor", currency),
          blobName: readNullableText(each["blob_name"], "invoice.blob_name"),
        };
      });
    },

    async buyCredit(request: TopUpRequest): Promise<TopUpOutcomeView> {
      const data = readRecord(
        readRecord(
          assertOk(
            await client.createTopUp({
              body: {
                amount_minor: request.amount.amountMinor,
                currency: request.amount.currency,
                // A token from the hosted form. There is no card-number field on this wire.
                card_token: request.cardToken,
                save_card: request.saveCard,
                // FIX-MONEY C1: the quote the card form was opened for, and the total it opened with.
                billing_quote_id: request.billingQuoteId,
                amount_due_minor: request.amountDue.amountMinor,
              },
            }),
          ),
          "createTopUp",
        )["data"],
        "createTopUp.data",
      );
      const currency = readText(data["currency"], "topUp.currency");
      const status = readText(data["status"], "topUp.status");
      const balanceAfter = data["balance_after_minor"];
      return {
        chargeAttemptId: readText(data["charge_attempt_id"], "topUp.charge_attempt_id"),
        status: status === "error" ? "error" : readChargeState(status),
        amount: minorMoney(data, "amount_minor", currency),
        balanceAfter:
          balanceAfter === null || balanceAfter === undefined
            ? null
            : money(readWholeNumber(balanceAfter, "topUp.balance_after_minor"), currency as CurrencyCode),
        redirectUrl:
          typeof data["redirect_url"] === "string" ? data["redirect_url"] : null,
        declineCode: typeof data["decline_code"] === "string" ? data["decline_code"] : null,
        declineMessage:
          typeof data["decline_message"] === "string" ? data["decline_message"] : null,
        // The outcome is UNKNOWN. `merchant_ref` gives duplicate rejection, not
        // idempotency-key semantics, so this means "go look up what happened".
        pendingReconciliation: data["resolution"] === "pending_reconciliation",
      };
    },

    // --- workspaces and the developer surface: documented (P11-08) ----------------------------
    async listWorkspaces(): Promise<readonly WorkspaceView[]> {
      return readDataArray(assertOk(await client.listWorkspaces({})), "listWorkspaces").map(
        (one, index) => workspaceOfWire(readRecord(one, `workspace[${String(index)}]`)),
      );
    },

    async createWorkspace(draft): Promise<WorkspaceView> {
      return workspaceOfWire(
        readRecord(
          readRecord(
            assertOk(
              await client.createWorkspace({
                body: { name: draft.name, time_zone: draft.timeZone },
              }),
            ),
            "createWorkspace",
          )["data"],
          "createWorkspace.data",
        ),
      );
    },

    async getWorkspace(workspaceId: string): Promise<WorkspaceDetailView> {
      return workspaceDetailOfWire(
        readRecord(
          readRecord(
            assertOk(await client.getWorkspace({ path: { workspace_id: workspaceId } })),
            "getWorkspace",
          )["data"],
          "getWorkspace.data",
        ),
      );
    },

    async renameWorkspace(workspaceId: string, draft): Promise<WorkspaceDetailView> {
      assertOk(
        await client.updateWorkspace({
          path: { workspace_id: workspaceId },
          body: { name: draft.name, time_zone: draft.timeZone },
        }),
      );
      // Re-read rather than trust the PATCH's projection: the detail response carries the
      // agents and the month's figures, and the update response deliberately does not.
      return workspaceDetailOfWire(
        readRecord(
          readRecord(
            assertOk(await client.getWorkspace({ path: { workspace_id: workspaceId } })),
            "getWorkspace",
          )["data"],
          "getWorkspace.data",
        ),
      );
    },

    async archiveWorkspace(workspaceId: string): Promise<void> {
      assertOk(await client.deleteWorkspace({ path: { workspace_id: workspaceId } }));
    },

    async listWorkspacePeople(workspaceId: string): Promise<WorkspacePeopleView> {
      const data = readRecord(
        readRecord(
          assertOk(await client.listWorkspaceMembers({ path: { workspace_id: workspaceId } })),
          "listWorkspaceMembers",
        )["data"],
        "listWorkspaceMembers.data",
      );
      const at = now();
      return {
        member: readArray(data["member"], "workspacePeople.member").map((one, index) =>
          memberOfWire(readRecord(one, `member[${String(index)}]`), at),
        ),
        invitation: readArray(data["invitation"], "workspacePeople.invitation").map(
          (one, index) => invitationOfWire(readRecord(one, `invitation[${String(index)}]`), at),
        ),
      };
    },

    async inviteToWorkspace(workspaceId: string, invitation): Promise<IssuedInvitationView> {
      const data = readRecord(
        readRecord(
          assertOk(
            await client.inviteWorkspaceMember({
              path: { workspace_id: workspaceId },
              // plan-014 B3: the fragment's role enum is the seven roles; until the next api-client regeneration the
              // generated body type still names Q10's four, so the role is passed as the wire value it is.
              body: { email: invitation.emailAddress, role: invitation.role as LegacyWireRole },
            }),
          ),
          "inviteWorkspaceMember",
        )["data"],
        "inviteWorkspaceMember.data",
      );
      return extendDisplayView(invitationOfWire(data, now()), {
        // The one place this value exists. It is not cached, not logged and not re-read.
        token: readText(data["token"], "invitation.token"),
      });
    },

    async revokeInvitation(workspaceId: string, invitationId: string): Promise<void> {
      assertOk(
        await client.revokeWorkspaceInvitation({
          path: { workspace_id: workspaceId, invitation_id: invitationId },
        }),
      );
    },

    async setMemberRole(
      workspaceId: string,
      membershipId: string,
      role: MemberRoleView,
    ): Promise<WorkspaceMemberView> {
      return memberOfWire(
        readRecord(
          readRecord(
            assertOk(
              await client.setWorkspaceMemberRole({
                path: { workspace_id: workspaceId, membership_id: membershipId },
                body: { role: role as LegacyWireRole },
              }),
            ),
            "setWorkspaceMemberRole",
          )["data"],
          "setWorkspaceMemberRole.data",
        ),
        now(),
      );
    },

    async removeMember(workspaceId: string, membershipId: string): Promise<void> {
      assertOk(
        await client.removeWorkspaceMember({
          path: { workspace_id: workspaceId, membership_id: membershipId },
        }),
      );
    },

    async listApiKeys(): Promise<readonly ApiKeyView[]> {
      const at = now();
      return readDataArray(assertOk(await client.listApiKeys()), "listApiKeys").map((one, index) =>
        apiKeyOfWire(readRecord(one, `apiKey[${String(index)}]`), at),
      );
    },

    async createApiKey(draft: ApiKeyDraft): Promise<IssuedApiKeyView> {
      const data = readRecord(
        readRecord(
          assertOk(await client.createApiKey({ body: { name: draft.name, scope: draft.scope } })),
          "createApiKey",
        )["data"],
        "createApiKey.data",
      );
      return extendDisplayView(apiKeyOfWire(data, now()), {
        // Shown once. Nothing here stores it, caches it or writes it to a log.
        key: readText(data["key"], "apiKey.key"),
      });
    },

    async revokeApiKey(apiKeyId: string): Promise<void> {
      assertOk(await client.revokeApiKey({ path: { api_key_id: apiKeyId } }));
    },

    async listWebhookEndpoints(): Promise<readonly WebhookEndpointView[]> {
      const body = readRecord(
        assertOk(await client.listWebhookEndpoints({ query: {} })),
        "listWebhookEndpoints",
      );
      return readArray(body["data"], "listWebhookEndpoints.data").map((one, index) => {
        const each = readRecord(one, `webhookEndpoint[${String(index)}]`);
        return {
          webhookEndpointId: readText(
            each["webhook_endpoint_id"],
            "webhookEndpoint.webhook_endpoint_id",
          ),
          url: readText(each["url"], "webhookEndpoint.url"),
          eventKind: readTextArray(each["event_kind"], "webhookEndpoint.event_kind"),
          enabled: readBoolean(each["enabled"], "webhookEndpoint.enabled"),
        };
      });
    },

    // --- Agent · Analytics, Versions and Review: documented since P11-01/02/03 -----------------
    async getVoiceAgentAnalytics(
      voiceAgentId: string,
      range: AnalyticsRange,
    ): Promise<VoiceAgentAnalyticsView> {
      const body = readRecord(
        assertOk(
          await client.getVoiceAgentAnalytics({
            path: { voice_agent_id: voiceAgentId },
            query: analyticsWindowOf(range, now()),
          }),
        ),
        "getVoiceAgentAnalytics",
      );
      // plan-021: the "Passes" block (counts, never money).
      return extendDisplayView(analyticsOfWire(body["data"], "analytics"), {
        voiceAgentPass: analyticsVoiceAgentPassOfWire(
          readRecord(body["data"], "analytics")["voice_agent_pass"],
          "analytics.voice_agent_pass",
        ),
        // plan-029 API-READ1: caller protection's refusals and ends, by reason.
        refusedByReason: refusedByReasonOfWire(readRecord(body["data"], "analytics")["refused_by_reason"], "analytics.refused_by_reason"),
        endedByReason: endedByReasonOfWire(readRecord(body["data"], "analytics")["ended_by_reason"], "analytics.ended_by_reason"),
        // plan-031 VMD-API: answered outbound conversations and those a voicemail answered.
        outbound: analyticsOutboundOfWire(readRecord(body["data"], "analytics")["outbound"], "analytics.outbound"),
      });
    },

    async listAgentVersions(voiceAgentId: string): Promise<readonly AgentVersionSummaryView[]> {
      const at = now();
      // TWO READS, DELIBERATELY. Which version is live is a property of the VoiceAgent
      // (`voice_agent.live_version_id`), not of a version row, and there is no `is_live`
      // column on `agent_version` — there must not be one, because it would be a second copy
      // of the publish decision that could disagree with the first. So the VoiceAgent is read
      // for the pointer and the versions for the rows.
      const voiceAgent = readRecord(
        assertOk(await client.getVoiceAgent({ path: { voice_agent_id: voiceAgentId } })),
        "getVoiceAgent",
      );
      const liveVersionId = readNullableText(
        readRecord(voiceAgent["data"], "getVoiceAgent.data")["live_version_id"],
        "getVoiceAgent.data.live_version_id",
      );
      const body = readRecord(
        assertOk(await client.listAgentVersions({ path: { voice_agent_id: voiceAgentId } })),
        "listAgentVersions",
      );
      return readArray(body["data"], "listAgentVersions.data").map((row, index) =>
        agentVersionOfWire(row, `agentVersion[${String(index)}]`, liveVersionId, at),
      );
    },

    publishAgentVersion: (voiceAgentId, agentVersionId, expectedLiveVersionSeq) =>
      publishOrRestore(client, "publish", voiceAgentId, agentVersionId, expectedLiveVersionSeq),
    restoreAgentVersion: (voiceAgentId, agentVersionId, expectedLiveVersionSeq) =>
      publishOrRestore(client, "restore", voiceAgentId, agentVersionId, expectedLiveVersionSeq),

    // plan-003 RC-47: the switch publishes a version, so it quotes the sequence a publish does.
    async setVoiceAgentRecording(voiceAgentId, recordingEnabled, expectedLiveVersionSeq) {
      const body = readRecord(
        assertOk(
          await client.updateVoiceAgent({
            path: { voice_agent_id: voiceAgentId },
            body: { recording_enabled: recordingEnabled, expected_live_version_seq: expectedLiveVersionSeq },
          }),
        ),
        "updateVoiceAgent",
      );
      return voiceAgentRecordingOfWire(body["data"], "updateVoiceAgent.data");
    },

    async listVoiceAgentTestSuites(voiceAgentId: string): Promise<readonly TestSuiteView[]> {
      const at = now();
      const body = readRecord(
        assertOk(
          await client.getVoiceAgentTestSuites({ path: { voice_agent_id: voiceAgentId } }),
        ),
        "getVoiceAgentTestSuites",
      );
      return readArray(body["data"], "getVoiceAgentTestSuites.data").map((row, index) =>
        testSuiteOfWire(row, `testSuite[${String(index)}]`, at),
      );
    },

    async listGaps(voiceAgentId: string): Promise<readonly GapView[]> {
      const at = now();
      const body = readRecord(
        // "all", then open, queued and folded: a saved answer stays on the screen, queued, until it
        // is published (2026-09-30) — the default `open` made it vanish the moment it was saved.
        // Folded rows stay too (plan-015 G9): the Review tab shows the ones whose version is still
        // a draft as "In a draft (vN)" and drops the rest.
        assertOk(await client.listGaps({ path: { voice_agent_id: voiceAgentId }, query: { state: "all" } })),
        "listGaps",
      );
      return readArray(body["data"], "listGaps.data")
        .map((row, index) => gapOfWire(row, `gap[${String(index)}]`, at))
        .filter((one) => one.state === "open" || one.state === "queued" || one.state === "folded");
    },

    async answerGap(voiceAgentId: string, gapId: string, answerText: string): Promise<GapView> {
      const at = now();
      const body = readRecord(
        assertOk(
          await client.answerGap({
            path: { voice_agent_id: voiceAgentId, gap_id: gapId },
            body: { answer_text: answerText },
          }),
        ),
        "answerGap",
      );
      return gapOfWire(body["data"], "gap", at);
    },

    async dismissGap(voiceAgentId: string, gapId: string): Promise<GapView> {
      const at = now();
      const body = readRecord(
        assertOk(
          await client.dismissGap({ path: { voice_agent_id: voiceAgentId, gap_id: gapId } }),
        ),
        "dismissGap",
      );
      return gapOfWire(body["data"], "gap", at);
    },

    async publishAnswers(voiceAgentId: string, expectedLiveVersionSeq: number): Promise<PublishedAnswersView> {
      const body = readRecord(
        assertOk(
          await client.publishAnswers({
            path: { voice_agent_id: voiceAgentId },
            body: { expected_live_version_seq: expectedLiveVersionSeq },
          }),
        ),
        "publishAnswers",
      );
      const data = readRecord(body["data"], "publishAnswers.data");
      return {
        liveVersionSeq: readWholeNumber(data["live_version_seq"], "publishAnswers.data.live_version_seq"),
        publishedGapCount: readArray(data["published_gap_id"], "publishAnswers.data.published_gap_id").length,
        answerCount: readWholeNumber(data["answer_count"], "publishAnswers.data.answer_count"),
      };
    },

    async listConversationFlags(voiceAgentId: string): Promise<readonly ConversationFlagView[]> {
      const at = now();
      const body = readRecord(
        assertOk(await client.listConversationFlags({ path: { voice_agent_id: voiceAgentId } })),
        "listConversationFlags",
      );
      return readArray(body["data"], "listConversationFlags.data").map((row, index) =>
        conversationFlagOfWire(row, `conversationFlag[${String(index)}]`, at),
      );
    },

    async resolveConversationFlag(
      voiceAgentId: string,
      conversationFlagId: string,
    ): Promise<ConversationFlagView> {
      const at = now();
      const body = readRecord(
        assertOk(
          await client.resolveConversationFlag({
            path: {
              voice_agent_id: voiceAgentId,
              conversation_flag_id: conversationFlagId,
            },
          }),
        ),
        "resolveConversationFlag",
      );
      return conversationFlagOfWire(body["data"], "conversationFlag", at);
    },

    // --- streams: documented paths ----------------------------------------------------------
    streamBuildJob: (buildJobId, onEvent) =>
      stream("streamBuildJob", { build_job_id: buildJobId }, onEvent),
    streamVoiceAgentLive: (voiceAgentId, onEvent) =>
      stream("streamVoiceAgentLive", { voice_agent_id: voiceAgentId }, onEvent),
    streamConversationTranscript: (conversationId, onEvent) =>
      stream("streamConversationTranscript", { conversation_id: conversationId }, onEvent),

    // --- the plan-002 areas (RB-03, IC-2) ---------------------------------------------------
    // Each refuses with `UndocumentedOperationError` until its unit implements it in its own
    // slice: the Test console, the hosted card form, notifications, first run and the dashboard,
    // and every P14 surface. The slices never import a `*-sandbox.ts` module.
    ...accountApiSlice(client, sliceContext),
    ...notificationApiSlice(client, sliceContext),
    ...workspaceHomeApiSlice(client, sliceContext),
    ...impersonationHandoffApiSlice(client),
    ...operatorActionConfirmationApiSlice(client),
    // plan-015 LIM (G11): "Calls at once · Change" on the agent page.
    ...channelUnitLimitApiSlice(client),
    ...mailLandingApiSlice(client),
    ...passwordResetApiSlice(client),
    ...sharedConfigurationApiSlice(client),
    ...purchaseTermsApiSlice(client),
    ...billingCardApiSlice(client, sliceContext),
    ...testCallApiSlice(client, sliceContext),
    ...contactListApiSlice(client, sliceContext),
    ...campaignApiSlice(client, sliceContext),
    ...surveyApiSlice(client, sliceContext),
    ...doNotCallApiSlice(client, sliceContext),
    ...complianceApiSlice(client, sliceContext),
    ...auditEntryApiSlice(client, sliceContext),
    ...optimisationReviewApiSlice(client, sliceContext),
    ...agentDocumentApiSlice(client, sliceContext),
    // Customer-portal review 2026-09-25: pause, resume, archive, rebuild, and the build read.
    ...voiceAgentLifecycleApiSlice(client, sliceContext),
    // Customer-portal review #75: edit a built agent — its answers, and the next version.
    ...voiceAgentEditApiSlice(client, sliceContext),
    // plan-003 RC-46: one audited play of a recording (design §8.8).
    ...recordingApiSlice(client),
    // 2026-09-29: the account-wide Live activity page — every agent's conversations in flight.
    ...liveActivityApiSlice(client, sliceContext),
    // Customer-portal review #127: the language the new-agent wizard opens on.
    ...wizardApiSlice(client),
    // Fix round 2026-09-25 (reviews #49, #115, #59): number and concurrency requests.
    ...pendingRequestApiSlice(client, sliceContext),
    // plan-004 WC-81: the Web chat page — the detail with every website widget, and its four writes.
    ...messagingApiSlice(client, sliceContext, settings.baseUrl),

    // --- Agent · Orders and Order detail (P12-03) ---------------------------------------------
    // The one gateway slice that resolves a shop before it can read anything: `/v1/orders/**`
    // is keyed by shop, the console is looking at one VoiceAgent, and the summary is the
    // operation that maps one to the other.
    ...orderBookApiGateway({
      client,
      baseUrl: settings.baseUrl,
      publicSiteBaseUrl: settings.publicSiteBaseUrl,
    }),
    // plan-005 OP-36: an order's online payment — resend, cancel the link, refund, switch to
    // hand-over (IC-52a) — and the shop's order stream the Orders screens re-read on (DD5-33).
    ...customerOrderPaymentApiSlice(client, stream),
    // plan-005 OP-38: the Payouts page, the destination with its step-up, and the online-payment
    // status the wizard and the Payments setting show (IC-52b, IC-59).
    ...payoutApiSlice(client),
    // plan-008 ST-UN: the account's own SIP trunks — all 21 IC-77 operations (design §22.13.2).
    ...sipTrunkApiSlice(client, sliceContext),
    // plan-008 ST-UN: on-demand outbound through the account's trunk — start, read, end (ST-AO's routes).
    ...outboundConversationApiSlice(client, sliceContext),
    // Design §26: a voice saying its language's sample line, fetched as audio (not through the client).
    // plan-014 A4: the scope-aware fetch, so the one hand-made request carries the generation too.
    ...voiceSampleApiSlice(settings.baseUrl, personCredential, scopeAwareFetch),
    // plan-014 REG (ED-15): organization, people, security and billing — each area's unit fills its slice.
    ...accountTeamApiSlice(client, sliceContext),
    // plan-014 B3: workspace funding, join rules, who may create, sign-up from an invitation.
    ...workspaceManagementApiSlice(client, sliceContext),
    ...personalSecurityApiSlice(client, sliceContext),
    ...accountSecurityApiSlice(client, sliceContext),
    // plan-014 C3: workspace and organization API keys — scope, expiry, networks, rotation.
    ...apiKeyApiSlice(client, sliceContext),
    // plan-014 INT (owner answer 47): the verified-domain offers to join an organization.
    ...domainJoinOfferApiSlice(client, sliceContext),
    ...billingAccountApiSlice(client, sliceContext),
    ...billingProfileApiSlice(client, sliceContext),
    ...billingOperationApiSlice(client, sliceContext),
    ...commercialOfferApiSlice(client, sliceContext),
    ...subscriptionApiSlice(client, sliceContext),
    ...entitlementApiSlice(client, sliceContext),
    ...financialDocumentApiSlice(client, sliceContext),
    ...postpaidApiSlice(client, sliceContext),
    ...resourceShareApiSlice(client, sliceContext),
    // plan-014 B4: archive, restore and close.
    ...lifecycleApiSlice(client, sliceContext),
    // plan-015 REG (ED-15): the wizard's menu preview (PRV).
    ...documentPreviewApiSlice(client, sliceContext),
    ...biConnectionApiSlice(client),
    // plan-017 WAPP-A: the talk rates every screen reads through `useTalkRates`, and the extra-time switch.
    ...talkRateApiSlice(client, sliceContext),
    // plan-018 REG: the website reading (API-AG) and the handoff setting (API-HO) — each unit fills its slice.
    ...websiteReadingApiSlice(client, sliceContext),
    ...handoffApiSlice(client, sliceContext),
    // plan-019 REG: the help assistant's reads (the grant, status, unread count, the person's own requests).
    ...helpApiSlice(client, sliceContext),
    // plan-021 REG: the pass targets and the pass referrers (WAPP-PASS).
    ...voiceAgentPassApiSlice(client, sliceContext),
    // plan-029 REG: caller protection and blocked numbers (WAPP-PROT), the access list (WAPP-ACCESS, WAPP-CONF), caller
    // confirmation (WAPP-CONF), visitor signing secrets (WAPP-VIS).
    ...callerProtectionApiSlice(client, sliceContext),
    ...blockedNumberApiSlice(client, sliceContext),
    ...partyAccessApiSlice(client, sliceContext),
    ...partyConfirmationApiSlice(client, sliceContext),
    ...visitorSigningSecretApiSlice(client, sliceContext),
    // plan-030 WAPP-Q: the survey question editor — read, check, save as a draft, download.
    ...surveyQuestionnaireApiSlice(client, sliceContext),
    // plan-034 WAPP-APPSIGNIN: the consent page's "Continue" (`/app-sign-in`).
    ...appSignInApiSlice(client, sliceContext),
  } satisfies ConsoleGateway;
}

/**
 * The History tab's four controls, as `/v1/conversations` query parameters.
 *
 * The range is turned into a `since` instant here rather than being sent as a word, because
 * "7 days" means something different to a browser in Athens and a server in Italy North, and
 * the browser is the one that knows which day the customer is looking at.
 */
function historyQueryOf(
  query: HistoryQuery,
  at: Date,
): Record<string, string> {
  const wire: Record<string, string> = {
    voice_agent_id: query.voiceAgentId,
    ...historyOutcomeWireOf(query.outcome),
    ...historyChannelLabelWireOf(query.channelLabel),
  };
  if (query.partyOf !== "") wire["party_of"] = query.partyOf;
  if (query.phoneNumberId !== undefined && query.phoneNumberId !== "") wire["phone_number_id"] = query.phoneNumberId;
  const days = RANGE_DAY[query.range];
  if (days !== null) {
    const since = new Date(at.getFullYear(), at.getMonth(), at.getDate() - days);
    wire["since"] = since.toISOString();
  }
  // Yesterday is the one closed window: it ends where today starts.
  if (query.range === "yesterday") {
    wire["until"] = new Date(at.getFullYear(), at.getMonth(), at.getDate()).toISOString();
  }
  if (query.search.trim().length > 0) wire["search"] = query.search.trim();
  return wire;
}

/** How many whole days back each range reaches. `null` is "all", which sends no `since`. */
const RANGE_DAY: Readonly<Record<HistoryQuery["range"], number | null>> = {
  today: 0,
  yesterday: 1,
  "7d": 7,
  "30d": 30,
  "90d": 90,
  all: null,
};


/** Builds a card from a wire row alone, for a template this console was built before. */
function cardOfWire(one: Readonly<Record<string, unknown>>): AgentTemplateCardView {
  return validatedDisplayView({
    code: readText(one["code"], "template.code"),
    name: readText(one["name"], "template.name"),
    get dek() { return readText(one["dek"], "template.dek"); },
    youAdd: readText(one["you_add"], "template.you_add"),
    iconName: readText(one["icon"], "template.icon"),
    // It came back from `GET /v1/templates`, whose SQL is `WHERE available = true`.
    available: true,
    contentTitle: readText(one["content_title"], "template.content_title"),
    contentDek: readText(one["content_dek"], "template.content_dek"),
    fieldSet: one["field_set"] as readonly AgentTemplateField[],
    defaultGreeting: readText(one["default_greeting"], "template.default_greeting"),
    defaultName: readText(one["default_name"], "template.default_name"),
    outcomeVocabulary: readTextArray(one["outcome_vocabulary"], "template.outcome_vocabulary"),
    get successMetricLabel() { return agentTemplateText(readText(one["success_metric_label"], "template.success_metric_label")); },
    referenceSchema: readText(one["reference_schema"], "template.reference_schema"),
    direction: directionOfWire(one["direction"], "template.direction"),
  });
}

/** `AgentTemplate.direction`: `inbound` or `outbound`, and nothing else is read as either. */
function directionOfWire(value: unknown, path: string): ConversationDirection {
  if (value === "inbound" || value === "outbound") return value;
  throw new MalformedResponseError(path, "inbound or outbound", value);
}

// ---------------------------------------------------------------------------------------
// Wire readers for the billing, workspace and developer surfaces (P11-07, P11-08)
// ---------------------------------------------------------------------------------------

/**
 * A money value from a `*_minor` integer plus a sibling `currency`.
 *
 * The billing wire carries one currency per object and several amounts beside it, so the
 * currency is read once and applied. Nothing here divides by a hundred: `Money` is minor
 * units all the way to the formatter, and a float introduced here would be a float in a
 * ledger.
 */
function minorMoney(
  one: Readonly<Record<string, unknown>>,
  field: string,
  currency: string,
): Money {
  return money(readWholeNumber(one[field], field), currency as CurrencyCode);
}

/** `billingBalance.workspace_budget`: `null` (no budget) or the verdict, never absent. */
function readWorkspaceBudget(value: unknown): WorkspaceBudgetView | null {
  if (value === null) return null;
  const budget = readRecord(value, "billingBalance.workspace_budget");
  const currency = readText(budget["currency"], "billingBalance.workspace_budget.currency");
  return {
    monthBudget: minorMoney(budget, "month_budget_minor", currency),
    spent: minorMoney(budget, "spent_minor", currency),
    hardStopEnabled: readBoolean(
      budget["hard_stop_enabled"],
      "billingBalance.workspace_budget.hard_stop_enabled",
    ),
    reached: readBoolean(budget["reached"], "billingBalance.workspace_budget.reached"),
  };
}

const REFUSAL_CODE: readonly BillingRefusalView["code"][] = [
  "credit_exhausted",
  "trial_exhausted",
  "hard_stop_reached",
  "budget_exceeded",
];
const REFUSAL_CAUSE: readonly BillingRefusalView["cause"][] = [
  "held_by_open_conversation",
  "credit_empty",
  "trial_used",
  "hard_stop",
  "budget_spent",
];

/** `refusal` of the balance (2026-10-04): `null` when a new conversation would be admitted. */
function readBillingRefusal(value: unknown, currency: string): BillingRefusalView | null {
  if (value === null) return null;
  const refusal = readRecord(value, "billingBalance.refusal");
  const code = REFUSAL_CODE.find((one) => one === refusal["code"]);
  const cause = REFUSAL_CAUSE.find((one) => one === refusal["cause"]);
  if (code === undefined || cause === undefined) {
    throw new Error(
      `billingBalance.refusal names ${JSON.stringify(refusal["code"])} / ${JSON.stringify(refusal["cause"])}, ` +
        "which the spec does not enumerate.",
    );
  }
  return {
    code,
    cause,
    held: minorMoney(refusal, "held_minor", currency),
    message: readText(refusal["message"], "billingBalance.refusal.message"),
  };
}

const CREDIT_STATE: readonly CreditState[] = ["trial", "in_credit", "exhausted", "hard_stopped"];

function readCreditState(value: unknown): CreditState {
  const raw = readText(value, "billingBalance.state");
  const found = CREDIT_STATE.find((one) => one === raw);
  if (found === undefined) {
    throw new Error(`"${raw}" is not a documented credit state; the spec enumerates four.`);
  }
  return found;
}

const CHARGE_STATE: readonly ChargeAttemptState[] = [
  "created",
  "sent",
  "succeeded",
  "declined",
  "requires_action",
  "requires_reauth",
  "ambiguous",
];

function readChargeState(value: unknown): ChargeAttemptState {
  const raw = readText(value, "chargeAttempt.state");
  const found = CHARGE_STATE.find((one) => one === raw);
  if (found === undefined) {
    throw new Error(`"${raw}" is not a documented charge_attempt state.`);
  }
  return found;
}

function readChargeKind(value: unknown): "cit" | "mit" | "refund" {
  const raw = readText(value, "chargeAttempt.kind");
  if (raw !== "cit" && raw !== "mit" && raw !== "refund") {
    throw new Error(`"${raw}" is not a documented charge_attempt kind.`);
  }
  return raw;
}

/**
 * The role type the generated client's people bodies carry until it is regenerated from B3's fragment (plan-014 §2.3's
 * api-client lock): Q10's four. A seven-role value is sent as the string it is; the server validates it.
 */
type LegacyWireRole = "owner" | "admin" | "member" | "viewer";

/**
 * One of the SEVEN workspace roles (plan-014 B3 widened the people routes from Q10's four). An unknown one raises; it
 * never defaults.
 */
function readMemberRole(value: unknown): MemberRoleView {
  const raw = readText(value, "member.role");
  const found = MEMBER_ROLE.find((one) => one === raw);
  if (found === undefined) {
    throw new Error(
      `"${raw}" is not one of the seven workspace roles. Defaulting an unknown role to the ` +
        "weakest one sounds safe and is not: it turns a storage fault into a permission.",
    );
  }
  return found;
}

const INVITATION_STATE_VALUE: readonly WorkspaceInvitationState[] = ["pending", "expired", "revoked"];

/** An invitation's state on the People tab (plan-014 B3). Unknown raises. */
function readInvitationState(value: unknown): WorkspaceInvitationState {
  const raw = readText(value, "invitation.state");
  const found = INVITATION_STATE_VALUE.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError("invitation.state", INVITATION_STATE_VALUE.join(" | "), raw);
  return found;
}

/**
 * One of the SEVEN workspace roles (plan-014 A3's presets beside Q10's four, design §27.5.1) — what
 * an auth_session and its organization choices name. Unknown raises, as above.
 */
function readWorkspaceRole(value: unknown, path: string): WorkspaceRoleView {
  const raw = readText(value, path);
  const found = MEMBER_ROLE.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, MEMBER_ROLE.join(" | "), raw);
  return found;
}

/** An organization role, or `null` where the person holds none. Unknown raises, as above. */
function readAccountRole(value: unknown, path: string): AccountRole | null {
  if (value === null) return null;
  const raw = readText(value, path);
  const found = ACCOUNT_ROLE.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, ACCOUNT_ROLE.join(" | "), raw);
  return found;
}

const ACCOUNT_STATE_VALUE: readonly AccountStateView[] = ["trial", "active", "past_due", "suspended", "closed", "closing"];

function readAccountState(value: unknown, path: string): AccountStateView {
  const raw = readText(value, path);
  const found = ACCOUNT_STATE_VALUE.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, ACCOUNT_STATE_VALUE.join(" | "), raw);
  return found;
}

const LANDING_VALUE: readonly LandingView[] = ["workspace", "finance", "organization", "selection"];

function readLanding(value: unknown): LandingView {
  const raw = readText(value, "authSession.landing");
  const found = LANDING_VALUE.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError("authSession.landing", LANDING_VALUE.join(" | "), raw);
  return found;
}

/** One organization of `account_choice` (plan-014 A2's new fields; the legacy pair is not read here). */
function accountChoiceOfWire(one: Readonly<Record<string, unknown>>): AccountChoiceView {
  const name = readText(one["name"], "accountChoice.name");
  return {
    accountId: readText(one["account_id"], "accountChoice.account_id"),
    name,
    initials: initialsOfName(name),
    state: readAccountState(one["state"], "accountChoice.state"),
    accountRole: readAccountRole(one["account_role"], "accountChoice.account_role"),
    workspace: readArray(one["workspace"], "accountChoice.workspace").map((each, index): WorkspaceChoiceView => {
      const workspace = readRecord(each, `accountChoice.workspace[${String(index)}]`);
      const workspaceName = readText(workspace["name"], "workspaceChoice.name");
      return {
        workspaceId: readText(workspace["workspace_id"], "workspaceChoice.workspace_id"),
        name: workspaceName,
        initials: initialsOfName(workspaceName),
        state: readText(workspace["state"], "workspaceChoice.state"),
        memberRole: readWorkspaceRole(workspace["member_role"], "workspaceChoice.member_role"),
        enterable: readBoolean(workspace["enterable"], "workspaceChoice.enterable"),
      };
    }),
  };
}

/**
 * The context's capability listing. A name this console does not know is left out rather than
 * raised: the listing only decides what to OFFER, a capability the console has no control for cannot
 * be offered anyway, and an API one release ahead must not take the whole console down.
 */
function capabilityOfWire(capability: Readonly<Record<string, unknown>>): ContextCapabilityView {
  return {
    account: readTextArray(capability["account"], "capability.account").filter(isAccountCapability),
    workspace: readTextArray(capability["workspace"], "capability.workspace").filter(isWorkspaceCapability),
  };
}

function nullableWhen(value: unknown, path: string, at: Date): string | null {
  const raw = readNullableText(value, path);
  return raw === null ? null : formatWhen(raw, at);
}

/**
 * The unconfigured auto-reload shape and the configured one, read as one type.
 *
 * `configured: false` comes back with no thresholds at all, and the view keeps them `null`
 * rather than inventing zeros — a zero threshold and "no policy yet" are different states and
 * the switch renders them differently.
 */
function autoReloadOfWire(value: unknown): AutoReloadView {
  const one = readRecord(value, "autoReloadPolicy");
  const configured = one["configured"] === true;
  if (!configured) {
    return {
      configured: false,
      enabled: false,
      threshold: null,
      topUp: null,
      monthlyCap: null,
      monthCharged: null,
      paymentMethodId: null,
      revision: null,
    };
  }
  const currency = readText(one["currency"], "autoReloadPolicy.currency");
  return {
    configured: true,
    enabled: readBoolean(one["enabled"], "autoReloadPolicy.enabled"),
    threshold: minorMoney(one, "threshold_minor", currency),
    topUp: minorMoney(one, "top_up_minor", currency),
    monthlyCap: minorMoney(one, "monthly_cap_minor", currency),
    monthCharged: minorMoney(one, "month_charged_minor", currency),
    paymentMethodId: readNullableText(
      one["payment_method_id"],
      "autoReloadPolicy.payment_method_id",
    ),
    revision: readWholeNumber(one["revision"], "autoReloadPolicy.revision"),
  };
}

function spendControlOfWire(value: unknown): SpendControlView {
  const one = readRecord(value, "budget");
  const configured = one["configured"] === true;
  const currency = readText(one["currency"], "budget.currency");
  return {
    configured,
    monthBudget: configured ? minorMoney(one, "month_budget_minor", currency) : null,
    alertThresholdPercent: configured
      ? readWholeNumber(one["alert_threshold_percent"], "budget.alert_threshold_percent")
      : null,
    hardStopEnabled: readBoolean(one["hard_stop_enabled"], "budget.hard_stop_enabled"),
    capBehavior: capBehaviorOfWire(one["cap_behavior"]),
    overage: one["overage_minor"] === null || one["overage_minor"] === undefined ? null : minorMoney(one, "overage_minor", currency),
  };
}

function capBehaviorOfWire(value: unknown): SpendControlView["capBehavior"] {
  if (value === null || value === undefined) return null;
  if (value === "hard_stop" || value === "capped_overage" || value === "notify_only") return value;
  throw new Error(`budget.cap_behavior ${JSON.stringify(value)} is not a spend cap behaviour`);
}

/** The two-letter mark the switcher and the workspace cards draw. */
function initialsOfName(name: string): string {
  const part = name
    .split(/[^\p{L}\p{N}]+/u)
    .filter((one) => one !== "")
    .slice(0, 2);
  if (part.length === 0) return "WT";
  return part.map((one) => one.slice(0, 1).toUpperCase()).join("");
}

function workspaceOfWire(one: Readonly<Record<string, unknown>>): WorkspaceView {
  const name = readText(one["name"], "workspace.name");
  const currency = readText(one["currency"], "workspace.currency");
  return {
    workspaceId: readText(one["workspace_id"], "workspace.workspace_id"),
    name,
    initials: initialsOfName(name),
    memberCount: readWholeNumber(one["member_count"], "workspace.member_count"),
    spendThisMonth: minorMoney(one, "month_priced_minor", currency),
  };
}

function workspaceDetailOfWire(one: Readonly<Record<string, unknown>>): WorkspaceDetailView {
  const summary = workspaceOfWire(one);
  const currency = readText(one["currency"], "workspace.currency");
  const state = readText(one["state"], "workspace.state");
  return validatedDisplayView({
    ...summary,
    slug: readText(one["slug"], "workspace.slug"),
    timeZone: readText(one["time_zone"], "workspace.time_zone"),
    state: state === "archived" ? "archived" : "active",
    voiceAgentCount: readWholeNumber(one["voice_agent_count"], "workspace.voice_agent_count"),
    // Seconds on the wire and in the view: the screen shows minutes and seconds
    // (`talkTimeText`), so nothing is rounded on the way.
    monthTalkSecond: readWholeNumber(one["month_billed_second"], "workspace.month_billed_second"),
    monthSpend: summary.spendThisMonth,
    get createdAtText() { return readText(one["created_at"], "workspace.created_at").slice(0, 10); },
    voiceAgent: readArray(one["voice_agent"], "workspace.voice_agent").map((each, index) => {
      const row = readRecord(each, `workspaceVoiceAgent[${String(index)}]`);
      return validatedDisplayView({
        voiceAgentId: readText(row["voice_agent_id"], "workspaceVoiceAgent.voice_agent_id"),
        displayName: readText(row["display_name"], "workspaceVoiceAgent.display_name"),
        state: readText(row["state"], "workspaceVoiceAgent.state"),
        agentTemplateName: readNullableText(
          row["agent_template_name"],
          "workspaceVoiceAgent.agent_template_name",
        ),
        monthTalkSecond: readWholeNumber(
          row["month_billed_second"],
          "workspaceVoiceAgent.month_billed_second",
        ),
        monthSpend: minorMoney(row, "month_priced_minor", currency),
        get createdAtText() { return dayTextOf(readText(row["created_at"], "workspaceVoiceAgent.created_at")); },
      });
    }),
  });
}

/** "23 Sep 2026" — a day as a person reads it, for telling two same-named rows apart. */
function dayTextOf(instant: string): string {
  return new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-GB"), { day: "numeric", month: "short", year: "numeric" }).format(
    new Date(instant),
  );
}

/**
 * Publish and restore, which are **one route pair over one handler** (P7-04).
 *
 * The server's `UPDATE … WHERE live_version_seq = $expected` is what makes two people
 * publishing at once a 409 rather than a lost decision, so the sequence the caller last read
 * is a required argument here too. The new sequence comes back, and the screen refetches on
 * a conflict rather than retrying with the same number — retrying would be asking to
 * overwrite a decision it has not seen.
 */
async function publishOrRestore(
  client: WeTalkApiClient,
  action: "publish" | "restore",
  voiceAgentId: string,
  agentVersionId: string,
  expectedLiveVersionSeq: number,
): Promise<number> {
  const request = {
    path: { voice_agent_id: voiceAgentId, agent_version_id: agentVersionId },
    body: { expected_live_version_seq: expectedLiveVersionSeq },
  };
  const body = readRecord(
    assertOk(
      action === "publish"
        ? await client.publishAgentVersion(request)
        : await client.restoreAgentVersion(request),
    ),
    `${action}AgentVersion`,
  );
  return readWholeNumber(
    readRecord(body["data"], `${action}AgentVersion.data`)["live_version_seq"],
    `${action}AgentVersion.data.live_version_seq`,
  );
}

/** `409 auth_session_superseded` — see `currentAuthSession` (plan-014 FIX2). */
function isSuperseded(answered: { readonly status: number; readonly body: unknown; readonly requestId: string | null }): boolean {
  return (
    answered.status === 409 &&
    new ApiError(answered.status, answered.body, answered.requestId).code === "auth_session_superseded"
  );
}

/** The auth_session body's `notice` (plan-014 ACT/FIX2): absent on every read but the one that moved it. */
function authSessionNoticeOf(raw: unknown): { readonly notice?: AuthSessionNoticeView } {
  if (raw === undefined || raw === null) return {};
  const code = readRecord(raw, "authSession.notice")["code"];
  if (code !== "membership_revoked") {
    throw new MalformedResponseError("authSession.notice.code", "membership_revoked", code);
  }
  return { notice: code };
}

const LIVE_MEDIA_CODEC = ["pcmu", "pcma", "g722", "l16", "opus", "amr_wb"] as const;
const LIVE_MEDIA_SAMPLE_RATE_HZ = [8000, 16000, 24000, 48000] as const;

/**
 * plan-015 LIVE (G14, design §28.5.14): a live board row's codec, rate and latency. Each is null when the server says
 * so or omits it (plan-015: "returns both or omits them" — omitted is not reported); a value outside migration 0127's
 * sets (or a latency that is not a whole number of ms) is a server defect and raises, rather than being shown.
 */
function liveBoardFactOfWire(
  one: Readonly<Record<string, unknown>>,
): Pick<LiveBoardConversationView, "mediaCodec" | "mediaSampleRateHz" | "latencyMs"> {
  const reported = (key: string): unknown => (key in one ? one[key] : null);
  const codec = reported("media_codec");
  const rate = reported("media_sample_rate_hz");
  const latency = reported("latency_ms");
  const mediaCodec = codec === null ? null : LIVE_MEDIA_CODEC.find((each) => each === codec);
  const mediaSampleRateHz = rate === null ? null : LIVE_MEDIA_SAMPLE_RATE_HZ.find((each) => each === rate);
  if (mediaCodec === undefined) throw new Error(`liveConversation.media_codec: ${JSON.stringify(codec)} is not a codec this console knows.`);
  if (mediaSampleRateHz === undefined) throw new Error(`liveConversation.media_sample_rate_hz: ${JSON.stringify(rate)} is not a sample rate.`);
  return {
    mediaCodec,
    mediaSampleRateHz,
    latencyMs: latency === null ? null : readWholeNumber(latency, "liveConversation.latency_ms"),
  };
}
